import { FaAngleRight, FaAngleLeft, FaTrash, FaAngleUp, FaAngleDown, FaExclamationTriangle } from 'react-icons/fa';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Navbar, Nav, Dropdown, Form, Button, Table, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import '../Pages/Activities.css'
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import {
    ADMIN_ONLY_BULK_REMOVE_DEFAULTS_TIP,
    ADMIN_ONLY_DEFAULT_REMOVE_TIP,
    ADMIN_ONLY_MULTIPLIER_TIP,
} from '../utils/adminLineItemLocks';
import PermissionGuard from './PermissionGuard';
import { PERMISSIONS } from '../constants/permissions';
import { formatCarePlanId, formatQuotationId, formatCreatedDate } from '../utils/displayIds';
import { readApiErrorMessage } from '../utils/apiErrorMessage';
import SearchableCheckboxPicker from './SearchableCheckboxPicker';
import LabourDifferenceSummary from './LabourDifferenceSummary';
import StaffTypeAbbr from './StaffTypeAbbr';
import { useStaffTypes } from '../hooks/useStaffTypes';
import './PlanQuoteDetail.css';
import './ZeroPriceWarning.css';
import {
    getZeroMultiplierMessage,
    isZeroPrice,
    resolveMultiplier,
    summarizeZeroMultiplierWarnings,
} from '../utils/zeroPriceWarnings';

function CarePlanDetail({
    embedded = false,
    onQuoteCreated,
    onEditStateChange,
    onOpenQuote,
    onPlanSaved,
    onHourTotalsChange,
    quoteHourTotals = null,
    showLabourDifference = false,
} = {}) {
    const { id, carePlanId } = useParams();
    const [searchParams] = useSearchParams();
    
    // Get tenant_id and site_id from URL params (passed when creating care plan from a specific site view)
    const urlTenantId = searchParams.get('tenantId');
    const urlSiteId = searchParams.get('siteId');
    
    // Get currentTenant from context - MUST be called at top level before any other hooks that use it
    const { currentTenant, tenants } = useTenant();
    const { getAbbreviation, getDisplayName } = useStaffTypes();
    const { hasPermission, hasRole } = usePermissions();
    const isAdmin = hasRole('Admin');
    
    // Use URL params if available, otherwise fall back to currentTenant context
    const effectiveTenantId = urlTenantId || currentTenant?.id;

    // Add custom styles for the dropdown
    const dropdownStyles = {
        optgroup: {
            fontWeight: 'bold',
            color: 'var(--theme-text-secondary)',
            backgroundColor: 'var(--theme-surface-muted)'
        },
        defaultOption: {
            backgroundColor: '#e8f5e8',
            color: '#2d5a2d',
            fontWeight: 'bold'
        },
        defaultOptionDisabled: {
            backgroundColor: '#e8f5e8',
            color: 'var(--theme-text-muted)',
            fontWeight: 'bold',
            textDecoration: 'line-through'
        }
    };

    const [carePlan, setCarePlan] = useState(null);
    const [activities, setActivities] = useState([]);
    const [lineItems, setLineItems] = useState([]);
    const [temp, setTemp] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredItems, setFilteredItems] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [edit, setEdit] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [selectedActivities, setSelectedActivities] = useState([]);
    const [bulkRemoveActivityIds, setBulkRemoveActivityIds] = useState(new Set());
    const [noActivities, setNoActivities] = useState(false);

    const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', confirmVariant: 'danger' });
    const showToast = (message, variant = 'success') => setToast({ show: true, message, variant });
    const showConfirm = (message, onConfirm, { title = 'Confirm', confirmLabel = 'Confirm', confirmVariant = 'danger' } = {}) =>
        setConfirmModal({ show: true, title, message, onConfirm, confirmLabel, confirmVariant });
    useEffect(() => {
        if (!toast.show) return;
        const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
        return () => clearTimeout(timer);
    }, [toast.show]);
    const [creatingCarePlan, setCreatingCarePlan] = useState(false);
    const [creatingQuote, setCreatingQuote] = useState(false);
    const [showQuoteConfirmation, setShowQuoteConfirmation] = useState(false);
    const [customerDetails, setCustomerDetails] = useState(null);
    const [careTypes, setCareTypes] = useState([]); // ADDED state for all care types
    const [defaultActivities, setDefaultActivities] = useState([]);
    const [carePlanTenantSite, setCarePlanTenantSite] = useState(null); // State for tenant/site info
    const [sortedActivities, setSortedActivities] = useState([]);
    const [activeQuote, setActiveQuote] = useState(null); // State for active quote
    const [isLoading, setIsLoading] = useState(true); // Loading state for initial data fetch
    
    // State for fetched tenant/site names (when we have IDs but not names)
    const [displayTenantName, setDisplayTenantName] = useState(null);
    const [displaySiteName, setDisplaySiteName] = useState(null);

    const [isSaving, setIsSaving] = useState(false);

    // Removed plans (status 2) are view-only: no edits or quote creation
    const isRemovedPlan = Number(carePlan?.status) === 2;

    // Report edit/dirty state to the shared Service Plan / Quote shell
    useEffect(() => {
        if (!onEditStateChange) return;
        onEditStateChange({ editing: edit, dirty: edit });
    }, [edit, onEditStateChange]);

    // Force view-only if plan is removed
    useEffect(() => {
        if (isRemovedPlan && edit) {
            setEdit(false);
        }
    }, [isRemovedPlan, edit]);

    // Site selection modal for "Create New Plan" / quote when user belongs to multiple sites
    const [siteSelectModal, setSiteSelectModal] = useState({ show: false, sites: [], purpose: 'plan' });
    const [pendingQuoteSite, setPendingQuoteSite] = useState(null);

    const baseActivitiesColumnClasses = [
        'col-activity',
        'col-multiplier',
        'col-frequency',
        'col-sn-day',
        'col-sn-night',
        'col-ena-day',
        'col-ena-night',
        'col-cw-day',
        'col-cw-night',
    ];

    const expandedActivitiesColumnClasses = [
        'col-benefit-body-pct',
        'col-benefit-body-hours',
        'col-benefit-mind-pct',
        'col-benefit-mind-hours',
        'col-benefit-social-pct',
        'col-benefit-social-hours',
        'col-care-type',
        'col-general-category',
    ];

    const renderActivitiesColGroup = (includeActionsColumn = false) => (
        <colgroup>
            {includeActionsColumn && <col className="col-actions" />}
            {baseActivitiesColumnClasses.map((colClass) => (
                <col key={colClass} className={colClass} />
            ))}
            {expandedActivitiesColumnClasses.map((colClass) => (
                <col
                    key={colClass}
                    className={`${colClass} ${collapsed ? 'is-collapsed' : ''}`}
                />
            ))}
        </colgroup>
    );

    const [totals, setTotals] = useState({
        sn_hours_day: 0,
        sn_hours_night: 0,
        ena_hours_day: 0,
        ena_hours_night: 0,
        cw_hours_day: 0,
        cw_hours_night: 0,
    });

    const [benefitsTotals, setbenefitsTotals] = useState({
        benefit_body: 0,
        benefit_mind: 0,
        benefit_social: 0
    });

    // Report live plan hour totals to the shell sticky labour-difference bar (minutes → hours)
    useEffect(() => {
        if (typeof onHourTotalsChange !== 'function') return;
        onHourTotalsChange('plan', {
            sn_hours_day: (Number(totals.sn_hours_day) || 0) / 60,
            sn_hours_night: (Number(totals.sn_hours_night) || 0) / 60,
            ena_hours_day: (Number(totals.ena_hours_day) || 0) / 60,
            ena_hours_night: (Number(totals.ena_hours_night) || 0) / 60,
            cw_hours_day: (Number(totals.cw_hours_day) || 0) / 60,
            cw_hours_night: (Number(totals.cw_hours_night) || 0) / 60,
        });
    }, [totals, onHourTotalsChange]);

    const planHoursForDiff = {
        sn_hours_day: (Number(totals.sn_hours_day) || 0) / 60,
        sn_hours_night: (Number(totals.sn_hours_night) || 0) / 60,
        ena_hours_day: (Number(totals.ena_hours_day) || 0) / 60,
        ena_hours_night: (Number(totals.ena_hours_night) || 0) / 60,
        cw_hours_day: (Number(totals.cw_hours_day) || 0) / 60,
        cw_hours_night: (Number(totals.cw_hours_night) || 0) / 60,
    };

    const formatPlanHours = (value) => (Number(value) || 0).toFixed(2);

    const planHoursSummaryTable = (
        <Table striped bordered hover>
            <thead>
                <tr>
                    <th colSpan="4" style={{ textAlign: 'center' }}>
                        <strong>Summary of Hours</strong>
                    </th>
                </tr>
                <tr>
                    <th></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Hours" /></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Hours" /></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Hours" /></th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Day</td>
                    <td>{formatPlanHours(planHoursForDiff.sn_hours_day)}</td>
                    <td>{formatPlanHours(planHoursForDiff.ena_hours_day)}</td>
                    <td>{formatPlanHours(planHoursForDiff.cw_hours_day)}</td>
                </tr>
                <tr>
                    <td>Night</td>
                    <td>{formatPlanHours(planHoursForDiff.sn_hours_night)}</td>
                    <td>{formatPlanHours(planHoursForDiff.ena_hours_night)}</td>
                    <td>{formatPlanHours(planHoursForDiff.cw_hours_night)}</td>
                </tr>
                <tr>
                    <td>Total</td>
                    <td>{formatPlanHours(planHoursForDiff.sn_hours_day + planHoursForDiff.sn_hours_night)}</td>
                    <td>{formatPlanHours(planHoursForDiff.ena_hours_day + planHoursForDiff.ena_hours_night)}</td>
                    <td>{formatPlanHours(planHoursForDiff.cw_hours_day + planHoursForDiff.cw_hours_night)}</td>
                </tr>
            </tbody>
        </Table>
    );

    const labourDifferenceBlock = showLabourDifference && quoteHourTotals ? (
        <div className="pqd-summary-grid-span">
            <LabourDifferenceSummary
                planHours={planHoursForDiff}
                quoteHours={quoteHourTotals}
            />
        </div>
    ) : null;

    const quotationHoursBlock = showLabourDifference && quoteHourTotals ? (
        <Table striped bordered hover>
            <thead>
                <tr>
                    <th colSpan="4" style={{ textAlign: 'center' }}>
                        <strong>Summary of Quotation Hours</strong>
                    </th>
                </tr>
                <tr>
                    <th></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Hours" /></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Hours" /></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Hours" /></th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Day</td>
                    <td>{(Number(quoteHourTotals.sn_hours_day) || 0).toFixed(2)}</td>
                    <td>{(Number(quoteHourTotals.ena_hours_day) || 0).toFixed(2)}</td>
                    <td>{(Number(quoteHourTotals.cw_hours_day) || 0).toFixed(2)}</td>
                </tr>
                <tr>
                    <td>Night</td>
                    <td>{(Number(quoteHourTotals.sn_hours_night) || 0).toFixed(2)}</td>
                    <td>{(Number(quoteHourTotals.ena_hours_night) || 0).toFixed(2)}</td>
                    <td>{(Number(quoteHourTotals.cw_hours_night) || 0).toFixed(2)}</td>
                </tr>
                <tr>
                    <td>Total</td>
                    <td>{((Number(quoteHourTotals.sn_hours_day) || 0) + (Number(quoteHourTotals.sn_hours_night) || 0)).toFixed(2)}</td>
                    <td>{((Number(quoteHourTotals.ena_hours_day) || 0) + (Number(quoteHourTotals.ena_hours_night) || 0)).toFixed(2)}</td>
                    <td>{((Number(quoteHourTotals.cw_hours_day) || 0) + (Number(quoteHourTotals.cw_hours_night) || 0)).toFixed(2)}</td>
                </tr>
            </tbody>
        </Table>
    ) : null;

    const handleSearchChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        const filtered = lineItems.filter(item =>
            (item.ActivityName || item.activity_name)?.toLowerCase().includes(value)
        );
        setFilteredItems(filtered);
    };

    const handleSort = (key, isNumeric = false) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }

        const sortedData = [...filteredItems].sort((a, b) => {
            if (isNumeric) {
                return direction === 'ascending'
                    ? (a[key] || 0) - (b[key] || 0)
                    : (b[key] || 0) - (a[key] || 0);
            } else {
                return direction === 'ascending'
                    ? (a[key] || '').toString().localeCompare((b[key] || '').toString())
                    : (b[key] || '').toString().localeCompare((a[key] || '').toString());
            }
        });

        setFilteredItems(sortedData);
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? <FaAngleUp /> : <FaAngleDown />;
        }
        return null;
    };



    useEffect(() => {
        const fetchActivities = async () => {
            try {
                const tenantIdParam = effectiveTenantId ? `&tenant_id=${effectiveTenantId}` : '';
                const siteIdParam = urlSiteId ? `&site_id=${urlSiteId}` : '';

                // Fetch all activities - include tenant_id, site_id and customer_id for filtering
                const activitiesUrl = `${API_BASE_URL}/careitemactivities?customer_id=${id}${tenantIdParam}${siteIdParam}`;
                const allActivitiesResponse = await fetchWithAuth(activitiesUrl);
                const allActivitiesData = await allActivitiesResponse.json();

                if (Array.isArray(allActivitiesData)) {
                    // Deduplicate by activity_id, preferring the entry whose tenant_site_id
                    // matches the current site so the site filter below doesn't drop it.
                    const currentSiteIdForDedup = urlSiteId || carePlanTenantSite?.tenant_site_id;
                    const uniqueActivityMap = new Map();
                    allActivitiesData.forEach(activity => {
                        const activityId = activity.activity_id || activity.ActivityID;
                        if (!activityId) return;
                        const tSiteId = activity.tenant_site_id || activity.metadata?.tenant_site?.id;
                        const matchesSite = tSiteId === currentSiteIdForDedup;
                        if (!uniqueActivityMap.has(activityId) || matchesSite) {
                            uniqueActivityMap.set(activityId, activity);
                        }
                    });
                    let uniqueActivities = Array.from(uniqueActivityMap.values());
                    
                    // Filter activities by site_id to only show activities for the current site
                    // This ensures activities from other sites don't appear when creating/editing a care plan
                    // For new care plans: use urlSiteId from URL params
                    // For existing care plans: use tenant_site_id which maps to metadata.tenant_site.id on activities
                    // Note: carePlanTenantSite.site_id may be undefined, but tenant_site_id is available
                    const currentSiteId = urlSiteId || carePlanTenantSite?.tenant_site_id;
                    // Filter activities by site_id to only show activities for the current site
                    // This ensures activities from other sites don't appear when creating/editing a care plan
                    // For new care plans: use urlSiteId from URL params
                    // For existing care plans: use tenant_site_id which maps to metadata.tenant_site.id on activities
                    // IMPORTANT FIX: Include BOTH site-specific activities AND global activities (no site_id)
                    // Global/default activities from flow_care_item_activities may not have site associations
                    // They should NOT be filtered out - they apply to all sites
                    if (currentSiteId) {
                        const beforeFilterCount = uniqueActivities.length;
                        
                        uniqueActivities = uniqueActivities.filter(activity => {
                            // Check multiple possible locations for the site_id
                            const activitySiteId = activity.site_id || activity.SiteID;
                            const metadataSiteId = activity.metadata?.tenant_site?.site_id;
                            const activityTenantSiteId = activity.tenant_site_id || activity.TenantSiteID;
                            const metadataTenantSiteId = activity.metadata?.tenant_site?.id;
                            
                            // Check if any ID matches the current site
                            const hasMatchingSiteId =
                                activitySiteId === currentSiteId ||
                                metadataSiteId === currentSiteId ||
                                activityTenantSiteId === currentSiteId ||
                                metadataTenantSiteId === currentSiteId;
                            
                            // Check if activity has any site_id at all (global activities have no site)
                            const hasNoSiteId = !activitySiteId && !metadataSiteId && !activityTenantSiteId && !metadataTenantSiteId;
                            
                            // INCLUDE if: matches current site OR has no site_id (global default)
                            // EXCLUDE only if: has a site_id that doesn't match current site
                            const shouldInclude = hasMatchingSiteId || hasNoSiteId;
                            
                            if (hasNoSiteId) {
                                // Include global activity
                            }
                            
                            return shouldInclude;
                        });
                        
                    }
                    
                    // Fetch default activities for the customer's care type if available
                    let defaultActivitiesData = [];
                    const careTypeId = customerDetails?.customer_care_type_id;
                    
                    if (careTypeId) {
                        try {
                            const tenantIdParam = effectiveTenantId ? `&tenant_id=${effectiveTenantId}` : '';
                            const siteIdParam = urlSiteId ? `&site_id=${urlSiteId}` : '';
                            const defaultActivitiesResponse = await fetchWithAuth(`${API_BASE_URL}/careitemactivities?customer_care_type_id=${String(careTypeId)}&customer_id=${id}${tenantIdParam}${siteIdParam}`);
                            const defaultData = await defaultActivitiesResponse.json();
                            if (Array.isArray(defaultData)) {
                                // Also dedupe default activities, preferring the current-site entry
                                const uniqueDefaultsMap = new Map();
                                defaultData.forEach(activity => {
                                    const activityId = activity.activity_id || activity.ActivityID;
                                    if (!activityId) return;
                                    const tSiteId = activity.tenant_site_id || activity.metadata?.tenant_site?.id;
                                    const matchesSite = tSiteId === currentSiteIdForDedup;
                                    if (!uniqueDefaultsMap.has(activityId) || matchesSite) {
                                        uniqueDefaultsMap.set(activityId, activity);
                                    }
                                });
                                let filteredDefaults = Array.from(uniqueDefaultsMap.values());
                                
                                // IMPORTANT FIX: Include BOTH site-specific AND global default activities
                                if (currentSiteId) {
                                    const beforeDefaultsFilter = filteredDefaults.length;
                                    filteredDefaults = filteredDefaults.filter(activity => {
                                        // Check multiple possible locations for the site_id
                                        const activitySiteId = activity.site_id || activity.SiteID;
                                        const metadataSiteId = activity.metadata?.tenant_site?.site_id;
                                        const activityTenantSiteId = activity.tenant_site_id || activity.TenantSiteID;
                                        const metadataTenantSiteId = activity.metadata?.tenant_site?.id;
                                        
                                        const hasMatchingSiteId =
                                            activitySiteId === currentSiteId ||
                                            metadataSiteId === currentSiteId ||
                                            activityTenantSiteId === currentSiteId ||
                                            metadataTenantSiteId === currentSiteId;
                                        
                                        // ALSO include activities with no site_id (global defaults)
                                        const hasNoSiteId = !activitySiteId && !metadataSiteId && !activityTenantSiteId && !metadataTenantSiteId;
                                        
                                        return hasMatchingSiteId || hasNoSiteId;
                                    });
                                }
                                
                                defaultActivitiesData = filteredDefaults;
                            }
                        } catch (error) {
                            console.error('Failed to fetch default activities:', error);
                        }
                    } else {
                    }
                    
                    // Get IDs of default activities
                    const defaultActivityIds = defaultActivitiesData.map(activity => activity.activity_id);
                    setDefaultActivities(defaultActivityIds);
                    
                    // Sort activities: defaults first, then alphabetically
                    const sortedData = uniqueActivities.sort((a, b) => {
                        const aIsDefault = defaultActivityIds.includes(a.activity_id);
                        const bIsDefault = defaultActivityIds.includes(b.activity_id);
                        
                        if (aIsDefault && !bIsDefault) return -1;
                        if (!aIsDefault && bIsDefault) return 1;
                        
                        return (a.activity_name || '').localeCompare(b.activity_name || '');
                    });
                    
                    setActivities(sortedData);
                    setSortedActivities(sortedData);
                } else {
                    console.error('Unexpected data format for activities:', allActivitiesData);
                    setActivities([]);
                    setSortedActivities([]);
                }

                if (allActivitiesData.length === 0) {
                    setNoActivities(true);
                }
            } catch (error) {
                console.error('Failed to fetch activities:', error);
                setActivities([]);
                setSortedActivities([]);
            }
        };

        fetchActivities();
    }, [customerDetails, effectiveTenantId, urlSiteId, carePlanTenantSite]); // Runs when customer details, tenant, site, or care plan site changes

    // Fetch care plan AFTER activities are loaded
    useEffect(() => {
        // Check if we're creating a new care plan (URL ends with /new OR carePlanId is 'new' OR carePlanId is undefined when on /new path)
        const isNewCarePlan = carePlanId === 'new' ||
                              window.location.pathname.endsWith('/new') ||
                              carePlanId === undefined;
        
        if (isNewCarePlan) {
            setCreatingCarePlan(true);
            setIsLoading(false); // No care plan to fetch, so loading is done
            return;
        }
        
        const fetchCarePlan = async () => {
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${carePlanId}`);
                if (response.ok) {
                    const data = await response.json();
                    
                    // Handle both lineItems and line_items from API response
                    const rawLineItems = data.lineItems || data.line_items || [];
                    
                    // Map the lineItems to include activity names and customer_care_type_id from the activities list
                    // Activities may not be loaded yet, so handle that gracefully
                    const mappedLineItems = rawLineItems.map(item => {
                        const matchingActivity = (() => {
                            if (item.activity_key) {
                                const byKey = activities.find(a => a.activity_key === item.activity_key);
                                if (byKey) return byKey;
                            }
                            const itemNorm = normalizeActivityId(item.activity_id ?? item.ActivityID)
                                || normalizeActivityId(item.activity_key);
                            return activities.find(a => {
                                const aId = a.activity_id ?? a.ActivityID;
                                const aKey = a.activity_key;
                                if (item.activity_key && (aKey === item.activity_key || aId === item.activity_key)) return true;
                                const aNorm = normalizeActivityId(aId) || normalizeActivityId(aKey);
                                return aNorm && itemNorm && aNorm === itemNorm;
                            });
                        })();
                        
                        // Get customer_care_type_id from the activity
                        const activityCareType = matchingActivity ? matchingActivity.customer_care_type_id : null;
                        
                        // Mirror QuotationDetails: fall back to top-level activity hour fields
                        // when the line item is missing a value (same as activityRecord?.sn_hours_day fallback).
                        const hourFields = ['sn_hours_day', 'sn_hours_night', 'ena_hours_day', 'ena_hours_night', 'cw_hours_day', 'cw_hours_night'];
                        const hourFallbacks = {};
                        if (matchingActivity) {
                            hourFields.forEach(field => {
                                if ((!item[field] || item[field] === 0) && matchingActivity[field]) {
                                    hourFallbacks[field] = matchingActivity[field];
                                }
                            });
                        }

                        return {
                            ...item,
                            ...hourFallbacks,
                            // Enrich with customer_care_type_id from the activity if not present on line item
                            // Handle empty string as "not set" - use activity's value if available
                            customer_care_type_id: (item.customer_care_type_id && item.customer_care_type_id !== '')
                                ? item.customer_care_type_id
                                : activityCareType,
                            ActivityName: item.ActivityName || item.activity_name ||
                                (matchingActivity ? (matchingActivity.activity_name || matchingActivity.ActivityName) : '') ||
                                'Unknown Activity',
                            activity_name: item.activity_name ||
                                (matchingActivity ? matchingActivity.activity_name : '') || '',
                            // Copy metadata from the matching activity when the line item has none or
                            // has an empty metadata object. This happens for older care plans where
                            // tenant_site_id was null, preventing the backend join from populating metadata.
                            metadata: (item.metadata && Object.keys(item.metadata).length > 0)
                                ? item.metadata
                                : (matchingActivity ? matchingActivity.metadata : undefined),
                        };
                    });
                    
                    setCarePlan(data.carePlan);
                    
                    // Set tenant/site info from the care plan response
                    if (data.carePlan) {
                        setCarePlanTenantSite({
                            tenant_id: data.carePlan.tenant_id || data.carePlan.TenantID,
                            site_id: data.carePlan.site_id || data.carePlan.SiteID,
                            tenant_site_id: data.carePlan.tenant_site_id || data.carePlan.TenantSiteID,
                            tenant_name: data.carePlan.tenant_name || data.carePlan.TenantName,
                            site_name: data.carePlan.site_name || data.carePlan.SiteName
                        });
                    }
                    
                    setLineItems(mappedLineItems);
                    setTemp(mappedLineItems);
                    setFilteredItems(mappedLineItems);

                    // Calculate totals with frequency × multiplier — same formula as Overview
                    const lineItemsForTotals = mappedLineItems;
                    const calculatedTotals = lineItemsForTotals.reduce(
                        (totals, item) => {
                            const frequency = parseFloat(getFrequencyValue(item)) || 1;
                            const multiplier = getMultiplier(item.multiplier);
                            const scale = frequency * multiplier;
                            return {
                                sn_hours_day: totals.sn_hours_day + (parseFloat(getHoursValue(item, 'sn_hours_day') || 0) * scale),
                                sn_hours_night: totals.sn_hours_night + (parseFloat(getHoursValue(item, 'sn_hours_night') || 0) * scale),
                                ena_hours_day: totals.ena_hours_day + (parseFloat(getHoursValue(item, 'ena_hours_day') || 0) * scale),
                                ena_hours_night: totals.ena_hours_night + (parseFloat(getHoursValue(item, 'ena_hours_night') || 0) * scale),
                                cw_hours_day: totals.cw_hours_day + (parseFloat(getHoursValue(item, 'cw_hours_day') || 0) * scale),
                                cw_hours_night: totals.cw_hours_night + (parseFloat(getHoursValue(item, 'cw_hours_night') || 0) * scale),
                            };
                        },
                        {
                            sn_hours_day: 0,
                            sn_hours_night: 0,
                            ena_hours_day: 0,
                            ena_hours_night: 0,
                            cw_hours_day: 0,
                            cw_hours_night: 0,
                        }
                    );

                    setTotals(calculatedTotals);

                    // Calculate benefit totals - use consistent key handling
                    const calculatedBenefitTotals = lineItemsForTotals.reduce(
                        (benefitsTotals, item) => {
                            const multiplier = getMultiplier(item.multiplier);
                            const totalHours =
                                Number(getHoursValue(item, 'sn_hours_day')) +
                                Number(getHoursValue(item, 'sn_hours_night')) +
                                Number(getHoursValue(item, 'ena_hours_day')) +
                                Number(getHoursValue(item, 'ena_hours_night')) +
                                Number(getHoursValue(item, 'cw_hours_day')) +
                                Number(getHoursValue(item, 'cw_hours_night'));

                            return {
                                benefit_body: benefitsTotals.benefit_body + (totalHours * multiplier * ((Number(getBenefitValue(item, 'benefit_body')) / 100) || 0)),
                                benefit_mind: benefitsTotals.benefit_mind + (totalHours * multiplier * ((Number(getBenefitValue(item, 'benefit_mind')) / 100) || 0)),
                                benefit_social: benefitsTotals.benefit_social + (totalHours * multiplier * ((Number(getBenefitValue(item, 'benefit_social')) / 100) || 0)),
                            };
                        },
                        {
                            benefit_body: 0,
                            benefit_mind: 0,
                            benefit_social: 0,
                        }
                    );

                    // Format final values to 2 decimal places
                    setbenefitsTotals({
                        benefit_body: calculatedBenefitTotals.benefit_body.toFixed(2),
                        benefit_mind: calculatedBenefitTotals.benefit_mind.toFixed(2),
                        benefit_social: calculatedBenefitTotals.benefit_social.toFixed(2),
                    });

                    // Care plan loaded successfully, but active quote fetch will handle final loading state
                    // Don't set isLoading false here - wait for active quote or let active quote useEffect handle it

                } else if (response.status === 404) {
                    setCreatingCarePlan(true);
                    setIsLoading(false); // Loading complete even though care plan doesn't exist

                    // Fetch customer activities when care plan doesn't exist
                    try {
                        const response = await fetchWithAuth(`${API_BASE_URL}/care-recipients`);
                        if (!response.ok) {
                            console.error(`HTTP error! Status: ${response.status}`);
                            return;
                        }

                        const data = await response.json();
                        const customer = data.find((recipient) => recipient.customer_id === id);

                        if (!customer) {
                            console.error("Customer not found.");
                            return;
                        }

                        const customerActivities = activities.filter(activity => {
                            const actCareType = activity.customer_care_type_id;
                            const custCareType = String(customer.customer_care_type_id);
                            if (!actCareType) return false;
                            if (Array.isArray(actCareType)) {
                                return actCareType.map(String).includes(custCareType);
                            }
                            return String(actCareType).split(',').map(s => s.trim()).includes(custCareType);
                        });

                        // Map the activities to include both snake_case and PascalCase properties
                        // Also try other possible name fields
                        const mappedActivities = customerActivities.map(activity => {
                            return {
                                ...activity,
                                ActivityName: activity.activity_name || activity.ActivityName || activity.name || activity.Name || 'NO_NAME',
                                ActivityID: activity.activity_id || activity.ActivityID || '',
                                activity_id: activity.activity_id || activity.ActivityID || '',
                                activity_name: activity.activity_name || activity.name || '',
                            };
                        });

                        setLineItems(mappedActivities);
                        setFilteredItems(mappedActivities);
                    } catch (error) {
                        console.error('Error fetching customer data:', error);
                    }
                } else {
                    console.error('Failed to fetch care plan');
                }
            } catch (error) {
                console.error('An error occurred while fetching the care plan:', error);
            }
        };

        fetchCarePlan();
    }, [id, carePlanId]); // Runs only when id or carePlanId changes

    // Sync activity names to line items when activities are loaded/changed
    // This runs after activities are fetched to enrich line items with names
    // Updated to handle both string keys ("activity_4") and numeric IDs
    useEffect(() => {
        if (activities.length === 0) {
            return;
        }

        const enrichItem = (item) => {
            const itemActivityId = item.activity_id || item.ActivityID;
            const itemKey = item.activity_key;
            const itemNorm = normalizeActivityId(itemActivityId) || normalizeActivityId(itemKey);

            const matchingActivity = activities.find(a => {
                const aId = a.activity_id ?? a.ActivityID;
                const aKey = a.activity_key;
                if (itemKey && (aKey === itemKey || aId === itemKey)) return true;
                const aNorm = normalizeActivityId(aId) || normalizeActivityId(aKey);
                return aNorm && itemNorm && aNorm === itemNorm;
            });

            if (matchingActivity) {
                const activityCareType = matchingActivity.customer_care_type_id;
                const needsNameUpdate = !item.ActivityName || item.ActivityName === 'Unknown Activity' || item.ActivityName === 'Unnamed Activity'
                    || !item.activity_name;
                const needsCareTypeUpdate = (!item.customer_care_type_id || item.customer_care_type_id === '') &&
                                            (!item.metadata?.customer_care_type_id);
                const needsMetadataUpdate = (!item.metadata || Object.keys(item.metadata).length === 0) && !!matchingActivity.metadata;

                // Mirror QuotationDetails: also fall back to top-level activity hour fields
                // when the line item has no value for a given field.
                const hourFields = ['sn_hours_day', 'sn_hours_night', 'ena_hours_day', 'ena_hours_night', 'cw_hours_day', 'cw_hours_night'];
                const hourUpdates = {};
                hourFields.forEach(field => {
                    if ((!item[field] || item[field] === 0) && matchingActivity[field]) {
                        hourUpdates[field] = matchingActivity[field];
                    }
                });
                const needsHoursUpdate = Object.keys(hourUpdates).length > 0;

                if (needsNameUpdate || needsCareTypeUpdate || needsMetadataUpdate || needsHoursUpdate) {
                    return {
                        ...item,
                        ...hourUpdates,
                        ActivityName: needsNameUpdate
                            ? (matchingActivity.activity_name || matchingActivity.ActivityName || item.ActivityName)
                            : item.ActivityName,
                        activity_name: needsNameUpdate
                            ? (matchingActivity.activity_name || item.activity_name)
                            : item.activity_name,
                        customer_care_type_id: needsCareTypeUpdate && activityCareType
                            ? activityCareType
                            : (item.customer_care_type_id || item.metadata?.customer_care_type_id),
                        activity_key: item.activity_key || matchingActivity.activity_key || `activity_${itemActivityId}`,
                        metadata: needsMetadataUpdate ? matchingActivity.metadata : item.metadata,
                    };
                }
            }
            return item;
        };

        const prevMetaEmpty = (m) => !m || Object.keys(m).length === 0;
        const hourFields = ['sn_hours_day', 'sn_hours_night', 'ena_hours_day', 'ena_hours_night', 'cw_hours_day', 'cw_hours_night'];
        const itemChanged = (updated, prev) =>
            updated.ActivityName !== prev?.ActivityName ||
            (!prevMetaEmpty(updated.metadata) && prevMetaEmpty(prev?.metadata)) ||
            hourFields.some(f => updated[f] !== prev?.[f]);

        // Use functional updates so we always work with the latest lineItems,
        // avoiding stale-closure overwrites when a second activities fetch fires
        // after carePlanTenantSite is set (which re-runs this effect).
        setLineItems(prevLineItems => {
            if (prevLineItems.length === 0) return prevLineItems;
            const updated = prevLineItems.map(enrichItem);
            const hasChanges = updated.some((item, i) => itemChanged(item, prevLineItems[i]));
            return hasChanges ? updated : prevLineItems;
        });

        setFilteredItems(prevFilteredItems => {
            if (prevFilteredItems.length === 0) return prevFilteredItems;
            const updated = prevFilteredItems.map(enrichItem);
            const hasChanges = updated.some((item, i) => itemChanged(item, prevFilteredItems[i]));
            return hasChanges ? updated : prevFilteredItems;
        });

        setTemp(prevTemp => {
            if (prevTemp.length === 0) return prevTemp;
            const updated = prevTemp.map(enrichItem);
            const hasChanges = updated.some((item, i) => itemChanged(item, prevTemp[i]));
            return hasChanges ? updated : prevTemp;
        });
    }, [activities]); // Runs when activities are loaded/updated

    // FIX: Ensure default activities are properly identified when loading existing care plan
    // This runs when activities, lineItems, defaultActivities or customerDetails change
    // Updated to handle both string keys ("activity_4") and numeric IDs
    useEffect(() => {
        // Only run if we have activities and customerDetails but defaultActivities might not be populated yet
        if (activities.length > 0 && customerDetails && defaultActivities.length === 0) {
            
            // Get customer's care type
            const careTypeId = customerDetails?.customer_care_type_id;
            if (careTypeId) {
                // Filter activities by care type to find defaults
                const defaultForCareType = activities.filter(activity => {
                    const actCareType = activity.customer_care_type_id;
                    if (!actCareType) return false;
                    if (Array.isArray(actCareType)) {
                        return actCareType.map(String).includes(String(careTypeId));
                    }
                    return String(actCareType).split(',').map(s => s.trim()).includes(String(careTypeId));
                });
                
                if (defaultForCareType.length > 0) {
                    // Store both activity_id and activity_key for proper matching
                    const defaultIds = defaultForCareType.map(a => a.activity_id);
                    setDefaultActivities(defaultIds);
                }
            }
        }
    }, [activities, defaultActivities, customerDetails]);

    // Ensure line items have customer_care_type_id from activities.
    // Uses functional state updates so this effect never overwrites names set by the name-sync effect.
    useEffect(() => {
        if (activities.length === 0) return;

        const enrichCareType = (item) => {
            if (item.customer_care_type_id && item.customer_care_type_id !== '') return item;
            if (item.metadata?.customer_care_type_id) {
                return { ...item, customer_care_type_id: item.metadata.customer_care_type_id };
            }
            const activityId = item.activity_id || item.ActivityID;
            if (!activityId) return item;
            const itemNorm = normalizeActivityId(activityId);
            const match = activities.find(a => {
                const aNorm = normalizeActivityId(a.activity_id ?? a.ActivityID);
                return aNorm && itemNorm && aNorm === itemNorm;
            });
            return match?.customer_care_type_id ? { ...item, customer_care_type_id: match.customer_care_type_id } : item;
        };

        const applyEnrichment = (prev) => {
            if (prev.length === 0) return prev;
            const updated = prev.map(enrichCareType);
            const changed = updated.some((item, i) => item.customer_care_type_id !== prev[i]?.customer_care_type_id);
            return changed ? updated : prev;
        };

        setLineItems(applyEnrichment);
        setFilteredItems(applyEnrichment);
        setTemp(applyEnrichment);
    }, [activities]);

    useEffect(() => {
        const fetchCustomerDetails = async () => {
            if (!id) return; // Don't fetch if id is not available
            try {
                // *** UPDATED Endpoint: Changed from /customers/ to /care-recipients/ ***
                const response = await fetchWithAuth(`${API_BASE_URL}/care-recipients/${id}`);
                if (response.ok) {
                    const data = await response.json();
                    setCustomerDetails(data); // Assuming API returns the customer object directly
                } else {
                    console.error('Failed to fetch customer details:', response.status, await response.text());
                    setCustomerDetails(null); // Set to null on failure
                }
            } catch (error) {
                console.error('An error occurred while fetching customer details:', error);
                setCustomerDetails(null);
            }
        };

        fetchCustomerDetails();
    }, [id]); // Runs only when the customer ID changes

    // == ADDED useEffect to fetch all Care Types ==
    useEffect(() => {
        const fetchCareTypes = async () => {
            try {
                // *** UPDATED Endpoint: Changed from /customer-care-types to /customerCareTypes ***
                const response = await fetchWithAuth(`${API_BASE_URL}/customerCareTypes`);
                if (response.ok) {
                    const data = await response.json();
                    setCareTypes(data || []); // Assuming API returns an array
                } else {
                    console.error('Failed to fetch care types:', response.status, await response.text());
                    setCareTypes([]); // Set to empty array on failure
                }
            } catch (error) {
                console.error('An error occurred while fetching care types:', error);
                setCareTypes([]);
            }
        };

        fetchCareTypes();
    }, []); // Runs only once on component mount

    // Fetch active quote for the care plan - use actual ID from fetched care plan data
    useEffect(() => {
        if (!carePlan || (!carePlan.care_plan_id && !carePlan.CarePlanID)) {
            // If no care plan, we're done loading (either error or still fetching care plan)
            return;
        }
        
        // Get the actual care plan ID from the fetched data (supports both snake_case and PascalCase)
        const actualCarePlanId = carePlan.care_plan_id || carePlan.CarePlanID;
        
        if (!actualCarePlanId || actualCarePlanId === 'new') {
            setIsLoading(false); // No quote to fetch for new care plans
            return;
        }
        
        const fetchActiveQuote = async () => {
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/careplan/${actualCarePlanId}/latest-quotation`);
                
                if (response.ok) {
                    try {
                        const data = await response.json();
                        // Map the response to match the expected format (supports both snake_case and PascalCase)
                        const quotationId = data.quotationId || data.quotation_id;
                        const dateCreated = data.dateCreated || data.date_created;

                        if (data && quotationId) {
                            setActiveQuote({
                                quotation_id: quotationId,
                                date_created: dateCreated
                            });
                        } else {
                            setActiveQuote(null);
                        }
                    } catch (parseError) {
                        console.error('Error parsing quote response:', parseError);
                        setActiveQuote(null);
                    }
                } else if (response.status === 404) {
                    // No active quote found for this care plan
                    setActiveQuote(null);
                } else {
                    // Handle 500 or other errors gracefully
                    console.error('Error fetching active quote:', response.status, response.statusText);
                    setActiveQuote(null);
                }
            } catch (error) {
                console.error('Error fetching active quote:', error);
                setActiveQuote(null);
            } finally {
                // Loading is complete after attempting to fetch active quote
                setIsLoading(false);
            }
        };
        
        fetchActiveQuote();
    }, [carePlan]); // Runs when carePlan data changes

    // Helper function to resolve tenant name from context (no API call needed)
    const fetchTenantName = useCallback(async (tenantId) => {
        if (!tenantId) return null;
        const tenant = tenants.find(t =>
            String(t.tenant_id || t.id) === String(tenantId)
        );
        if (tenant) return tenant.tenant_name || tenant.name || null;
        // Fallback: fetch if not in context (e.g. admin viewing another tenant's data)
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/my-tenants`);
            if (response.ok) {
                const data = await response.json();
                const found = data.find(t =>
                    String(t.tenant_id) === String(tenantId) ||
                    String(t.id) === String(tenantId)
                );
                return found?.tenant_name || found?.name || null;
            }
        } catch (error) {
            console.error('Error fetching tenant name:', error);
        }
        return null;
    }, [tenants]);

    // Helper function to fetch site name
    const fetchSiteName = async (siteId, tenantId) => {
        if (!siteId) return null;
        try {
            // Use /sites?tenant_id={tenantId} endpoint
            const url = tenantId
                ? `${API_BASE_URL}/sites?tenant_id=${tenantId}`
                : `${API_BASE_URL}/sites`;
            const response = await fetchWithAuth(url);
            if (response.ok) {
                const data = await response.json();
                const site = data.find(s =>
                    String(s.site_id) === String(siteId) ||
                    String(s.id) === String(siteId)
                );
                return site?.name || site?.site_name || null;
            }
        } catch (error) {
            console.error('Error fetching site name:', error);
        }
        return null;
    };

    // Fetch tenant and site names when we have IDs but not names
    useEffect(() => {
        const fetchNames = async () => {
            // For existing care plans - use the tenant_site info from the plan
            // Only use carePlanTenantSite if it actually has IDs — it can be set as a truthy
            // empty object when no link exists, which would block the URL-params branch below
            if (carePlanTenantSite && (carePlanTenantSite.tenant_id || carePlanTenantSite.site_id || carePlanTenantSite.tenant_site_id)) {
                let tenantName = carePlanTenantSite.tenant_name;
                let siteName = carePlanTenantSite.site_name;

                // If names are missing, fetch them
                if ((!tenantName || tenantName === carePlanTenantSite.tenant_id) && carePlanTenantSite.tenant_id) {
                    tenantName = await fetchTenantName(carePlanTenantSite.tenant_id);
                }
                if ((!siteName || siteName === carePlanTenantSite.site_id) && carePlanTenantSite.site_id) {
                    siteName = await fetchSiteName(carePlanTenantSite.site_id, carePlanTenantSite.tenant_id);
                }

                setDisplayTenantName(tenantName);
                setDisplaySiteName(siteName);
            }
            // For new care plans - use URL params
            else if (urlTenantId || urlSiteId) {
                let tenantName = null;
                let siteName = null;

                if (urlTenantId) {
                    tenantName = await fetchTenantName(urlTenantId);
                }
                if (urlSiteId) {
                    siteName = await fetchSiteName(urlSiteId, urlTenantId);
                }

                setDisplayTenantName(tenantName);
                setDisplaySiteName(siteName);
            }
        };

        fetchNames();
    }, [carePlanTenantSite, urlTenantId, urlSiteId, fetchTenantName]);

    const handleActivityChange = (e) => {
        const selected = Array.from(e.target.selectedOptions)
            .map(opt => { try { return JSON.parse(opt.value); } catch { return null; } })
            .filter(Boolean);
        setSelectedActivities(selected);
    };

    const handleMultiplierChange = (index, value) => {
        if (!isAdmin && isDefaultActivity(lineItems[index])) return;
        // Allow empty input and allow any valid number including negative and zero
        let newMultiplier;
        if (value === '') {
            newMultiplier = '';
        } else {
            const parsed = parseFloat(value);
            newMultiplier = isNaN(parsed) ? 1 : parsed; // Only default to 1 if parsing fails
        }

        const updatedLineItems = [...lineItems];
        const oldItem = updatedLineItems[index];

        if (!oldItem) {
            return;
        }

        // Update the item with the new multiplier
        updatedLineItems[index] = {
            ...oldItem,
            multiplier: newMultiplier,
        };

        // Update the line items state
        setLineItems(updatedLineItems);
        setFilteredItems(updatedLineItems);
        
        // Recalculate totals with the updated items
        recalculateTotals(updatedLineItems);
    };


    // Add this function to handle when the input loses focus
    const handleMultiplierBlur = (index, value) => {
        if (!isAdmin && isDefaultActivity(lineItems[index])) return;
        const updatedLineItems = [...lineItems];

        // Only set the multiplier to 1 if the input is empty or invalid
        if (value === '' || isNaN(parseFloat(value))) {
            updatedLineItems[index].multiplier = 1;
        }

        setLineItems(updatedLineItems);
        setFilteredItems(updatedLineItems);
        recalculateTotals(updatedLineItems);
    };

    // Helper function to recalculate totals (matches Overview: frequency × minutes × multiplier)
    const recalculateTotals = (lineItems) => {
        const newTotals = lineItems.reduce((totals, item) => {
            const frequency = parseFloat(getFrequencyValue(item)) || 1;
            const multiplier = getMultiplier(item.multiplier);
            const scale = frequency * multiplier;

            const snDay = parseFloat(getHoursValue(item, 'sn_hours_day') || 0) || 0;
            const snNight = parseFloat(getHoursValue(item, 'sn_hours_night') || 0) || 0;
            const enaDay = parseFloat(getHoursValue(item, 'ena_hours_day') || 0) || 0;
            const enaNight = parseFloat(getHoursValue(item, 'ena_hours_night') || 0) || 0;
            const cwDay = parseFloat(getHoursValue(item, 'cw_hours_day') || 0) || 0;
            const cwNight = parseFloat(getHoursValue(item, 'cw_hours_night') || 0) || 0;

            return {
                sn_hours_day: totals.sn_hours_day + (snDay * scale),
                sn_hours_night: totals.sn_hours_night + (snNight * scale),
                ena_hours_day: totals.ena_hours_day + (enaDay * scale),
                ena_hours_night: totals.ena_hours_night + (enaNight * scale),
                cw_hours_day: totals.cw_hours_day + (cwDay * scale),
                cw_hours_night: totals.cw_hours_night + (cwNight * scale),
            };
        }, {
            sn_hours_day: 0,
            sn_hours_night: 0,
            ena_hours_day: 0,
            ena_hours_night: 0,
            cw_hours_day: 0,
            cw_hours_night: 0,
        });

        setTotals(newTotals);
    };

    // Keep totals in sync whenever line items change (enrichment, edits, load)
    useEffect(() => {
        recalculateTotals(lineItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineItems]);

    const handleRemoveLineItem = (index) => {
        const removedItem = lineItems[index];
        if (!removedItem) return;
        if (!isAdmin && isDefaultActivity(removedItem)) return;

        const updatedLineItems = lineItems.filter((_, i) => i !== index);

        const benefithoursToSubtract = {
            benefit_body: parseFloat(removedItem.benefit_body || 0),
            benefit_mind: parseFloat(removedItem.benefit_mind || 0),
            benefit_social: parseFloat(removedItem.benefit_social || 0),
        };

        setLineItems(updatedLineItems);
        setFilteredItems(updatedLineItems);
        recalculateTotals(updatedLineItems);

        // Calculate the total hours removed (incl. frequency, same as overview)
        const removedScale = (parseFloat(getFrequencyValue(removedItem)) || 1) * getMultiplier(removedItem.multiplier);
        const hoursToSubtract = {
            sn_hours_day: parseFloat(removedItem.sn_hours_day || 0) * removedScale,
            sn_hours_night: parseFloat(removedItem.sn_hours_night || 0) * removedScale,
            ena_hours_day: parseFloat(removedItem.ena_hours_day || 0) * removedScale,
            ena_hours_night: parseFloat(removedItem.ena_hours_night || 0) * removedScale,
            cw_hours_day: parseFloat(removedItem.cw_hours_day || 0) * removedScale,
            cw_hours_night: parseFloat(removedItem.cw_hours_night || 0) * removedScale,
        };
        const totalHoursRemoved = Object.values(hoursToSubtract).reduce((acc, curr) => acc + curr, 0);

        const newbenefitTotals = {
            benefit_body: parseFloat(benefitsTotals.benefit_body || 0) -
                (totalHoursRemoved * (benefithoursToSubtract.benefit_body / 100)),
            benefit_mind: parseFloat(benefitsTotals.benefit_mind || 0) -
                (totalHoursRemoved * (benefithoursToSubtract.benefit_mind / 100)),
            benefit_social: parseFloat(benefitsTotals.benefit_social || 0) -
                (totalHoursRemoved * (benefithoursToSubtract.benefit_social / 100)),
        };

        setbenefitsTotals({
            benefit_body: newbenefitTotals.benefit_body.toFixed(2),
            benefit_mind: newbenefitTotals.benefit_mind.toFixed(2),
            benefit_social: newbenefitTotals.benefit_social.toFixed(2),
        });
    };

    // Helper function to sort activities: defaults first, then alphabetically
    const sortActivitiesWithDefaultsFirst = (activities) => {
        return [...activities].sort((a, b) => {
            const aIsDefault = isDefaultActivity(a);
            const bIsDefault = isDefaultActivity(b);
            
            if (aIsDefault && !bIsDefault) return -1;
            if (!aIsDefault && bIsDefault) return 1;
            
            return (a.activity_name || '').localeCompare(b.activity_name || '');
        });
    };

    const handleAddLineItem = () => {
        if (selectedActivities.length === 0) {
            showToast('Please select at least one activity.', 'warning');
            return;
        }

        let newLineItems = [...lineItems];
        const prevCount = newLineItems.length;

        for (const activity of selectedActivities) {
            if (isActivityAlreadyAdded(activity) || newLineItems.some(li => String(li.activity_id) === String(activity.activity_id))) {
                continue;
            }

            const newItem = {
                ...activity,
                ActivityName: activity.activity_name || activity.ActivityName || activity.name || '',
                activity_name: activity.activity_name || activity.name || '',
                frequency: getFrequencyValue(activity) || 1,
                sn_hours_day: parseFloat(getHoursValue(activity, 'sn_hours_day') || 0).toFixed(2),
                sn_hours_night: parseFloat(getHoursValue(activity, 'sn_hours_night') || 0).toFixed(2),
                ena_hours_day: parseFloat(getHoursValue(activity, 'ena_hours_day') || 0).toFixed(2),
                ena_hours_night: parseFloat(getHoursValue(activity, 'ena_hours_night') || 0).toFixed(2),
                cw_hours_day: parseFloat(getHoursValue(activity, 'cw_hours_day') || 0).toFixed(2),
                cw_hours_night: parseFloat(getHoursValue(activity, 'cw_hours_night') || 0).toFixed(2),
                benefit_body: getBenefitValue(activity, 'benefit_body'),
                benefit_mind: getBenefitValue(activity, 'benefit_mind'),
                benefit_social: getBenefitValue(activity, 'benefit_social'),
            };

            newLineItems.push(newItem);
        }

        const updatedLineItems = sortActivitiesWithDefaultsFirst(newLineItems);
        setLineItems(updatedLineItems);
        setFilteredItems(updatedLineItems);
        recalculateTotals(updatedLineItems);

        const newBenefitTotals = updatedLineItems.reduce((acc, item) => {
            const multiplier = getMultiplier(item.multiplier);
            return {
                benefit_body: acc.benefit_body + calculateBenefit(item, 'benefit_body') * multiplier,
                benefit_mind: acc.benefit_mind + calculateBenefit(item, 'benefit_mind') * multiplier,
                benefit_social: acc.benefit_social + calculateBenefit(item, 'benefit_social') * multiplier,
            };
        }, { benefit_body: 0, benefit_mind: 0, benefit_social: 0 });

        setbenefitsTotals({
            benefit_body: newBenefitTotals.benefit_body.toFixed(2),
            benefit_mind: newBenefitTotals.benefit_mind.toFixed(2),
            benefit_social: newBenefitTotals.benefit_social.toFixed(2),
        });

        const addedCount = newLineItems.length - prevCount;
        setSelectedActivities([]);
        if (addedCount > 0) {
            showToast(`${addedCount} activit${addedCount === 1 ? 'y' : 'ies'} added to service plan`);
        } else {
            showToast('No new activities added — all selected are already in this plan', 'warning');
        }
    };

    const handleBulkRemoveActivities = () => {
        if (bulkRemoveActivityIds.size === 0) return;
        const selectedItems = lineItems.filter(item => {
            const actId = String(item.activity_id || item.ActivityID || '');
            return bulkRemoveActivityIds.has(actId);
        });
        if (!isAdmin && selectedItems.some(item => isDefaultActivity(item))) {
            showToast(ADMIN_ONLY_BULK_REMOVE_DEFAULTS_TIP, 'warning');
            return;
        }
        const n = bulkRemoveActivityIds.size;
        showConfirm(
            `Remove ${n} selected activit${n === 1 ? 'y' : 'ies'}?`,
            () => {
                const updatedLineItems = lineItems.filter(item => {
                    const actId = String(item.activity_id || item.ActivityID || '');
                    return !bulkRemoveActivityIds.has(actId);
                });
                setLineItems(updatedLineItems);
                setFilteredItems(updatedLineItems);
                recalculateTotals(updatedLineItems);
                const newBenefitTotals = updatedLineItems.reduce((acc, item) => {
                    const multiplier = getMultiplier(item.multiplier);
                    return {
                        benefit_body: acc.benefit_body + calculateBenefit(item, 'benefit_body') * multiplier,
                        benefit_mind: acc.benefit_mind + calculateBenefit(item, 'benefit_mind') * multiplier,
                        benefit_social: acc.benefit_social + calculateBenefit(item, 'benefit_social') * multiplier,
                    };
                }, { benefit_body: 0, benefit_mind: 0, benefit_social: 0 });
                setbenefitsTotals({
                    benefit_body: newBenefitTotals.benefit_body.toFixed(2),
                    benefit_mind: newBenefitTotals.benefit_mind.toFixed(2),
                    benefit_social: newBenefitTotals.benefit_social.toFixed(2),
                });
                setBulkRemoveActivityIds(new Set());
            },
            { title: 'Remove Activities', confirmLabel: 'Remove' }
        );
    };



    const navigate = useNavigate();

    const getCustomerSitesForCurrentTenant = () => {
        const tenantId = currentTenant?.id;
        if (!tenantId) return [];
        const rawSites = customerDetails?.sites || [];
        return rawSites
            .filter(s => String(s.tenant_id) === String(tenantId))
            .map(s => ({
                siteId: s.site_id,
                siteName: s.site_name,
                tenantId: s.tenant_id,
                tenantName: s.tenant_name,
                tenantSiteId: s.tenant_site_id || s.id,
            }));
    };

    const handleCreateNewPlan = () => {
        const tenantId = currentTenant?.id;
        if (!tenantId) {
            showToast('No tenant context found. Please select a tenant first.', 'warning');
            return;
        }
        const available = getCustomerSitesForCurrentTenant();

        if (available.length === 0) {
            showToast('This customer has no sites linked to the current tenant. Assign a site on the customer record first.', 'warning');
        } else if (available.length === 1) {
            const s = available[0];
            navigate(`/careplans/${id}/new?tenantId=${s.tenantId}&siteId=${s.siteId}`);
        } else {
            setSiteSelectModal({ show: true, sites: available, purpose: 'plan' });
        }
    };

    const handleCreateQuoteClick = () => {
        if (isRemovedPlan) {
            showToast('Cannot create a quotation for a removed service plan.', 'warning');
            return;
        }

        // Prefer the plan's linked site; otherwise require a customer site so line items can be added
        if (carePlanTenantSite?.tenant_site_id) {
            setPendingQuoteSite(null);
            setShowQuoteConfirmation(true);
            return;
        }

        const available = getCustomerSitesForCurrentTenant();
        if (available.length === 0) {
            showToast('This service plan is not linked to a site. Create a new service plan with a site selected first.', 'warning');
            return;
        }
        if (available.length === 1) {
            setPendingQuoteSite(available[0]);
            setShowQuoteConfirmation(true);
            return;
        }
        setSiteSelectModal({ show: true, sites: available, purpose: 'quote' });
    };

    const handleCreateQuote = async () => {
        if (isRemovedPlan) {
            showToast('Cannot create a quotation for a removed service plan.', 'warning');
            setShowQuoteConfirmation(false);
            return;
        }
        setCreatingQuote(true);
        setShowQuoteConfirmation(false);
        try {
            const payload = {
                care_plan_id: carePlan.care_plan_id,
                customer_id: carePlan.customer_id,
                date_created: new Date().toISOString().split('T')[0],
                status: 0,
            };
            if (carePlanTenantSite?.tenant_site_id) {
                payload.tenant_site_id = carePlanTenantSite.tenant_site_id;
            } else if (pendingQuoteSite) {
                if (pendingQuoteSite.tenantSiteId) payload.tenant_site_id = pendingQuoteSite.tenantSiteId;
                if (pendingQuoteSite.tenantId) payload.tenant_id = pendingQuoteSite.tenantId;
                if (pendingQuoteSite.siteId) payload.site_id = pendingQuoteSite.siteId;
            }

            const response = await fetchWithAuth(`${API_BASE_URL}/createQuote`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();

                // Handle both camelCase (quotationId) and snake_case (quotation_id) from API response
                const quotationId = result.quotationId || result.quotation_id;
                
                if (!quotationId) {
                    console.error('Failed to get quotation ID from response:', result);
                    showToast('We created the quotation, but couldn’t open it. Please refresh and look for it in the list.', 'danger');
                    setCreatingQuote(false);
                    return;
                }

                if (embedded && typeof onQuoteCreated === 'function') {
                    onQuoteCreated(quotationId);
                } else {
                    navigate(`/careplans/${carePlan.customer_id}/${carePlan.care_plan_id}/quote/${quotationId}`);
                }

            } else {
                const message = await readApiErrorMessage(
                    response,
                    'We couldn’t create the quotation. Please try again.'
                );
                console.error('Failed to create quote:', response.status, message);
                showToast(message, 'danger');
            }
        } catch (error) {
            console.error('An error occurred while creating the quote:', error);
            showToast('We couldn’t create the quotation. Please check your connection and try again.', 'danger');
        } finally {
            setPendingQuoteSite(null);
            setCreatingQuote(false);
        }
    };

    const handleCancelQuote = () => {
        setShowQuoteConfirmation(false);
    };


    // Helper function to calculate the cost of each line item
    const [hourlyRates, setHourlyRates] = useState({ SN: 0, ENA: 0, CW: 0 });
    const fetchHourlyRates = async (dateCreated) => {
        try {
            const tenantParam = effectiveTenantId
                ? `&tenant_id=${encodeURIComponent(effectiveTenantId)}`
                : '';
            const response = await fetchWithAuth(
                `${API_BASE_URL}/staff/rates?date_created=${encodeURIComponent(dateCreated || '')}${tenantParam}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Check if the response is JSON
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error("Received non-JSON response");
            }

            const ratesArray = await response.json();

            // Convert the array to an object with SN, ENA, CW keys
            const mappedRates = (Array.isArray(ratesArray) ? ratesArray : []).reduce((acc, rate) => {
                const staffType = rate.StaffType || rate.staff_type || rate.flow_staff_type || rate.staff_type_code;
                const hourlyPrice = rate.StandardHourlyPrice || rate.StandardHourlyprice || rate.standard_hourly_price;
                if (staffType) {
                    acc[staffType] = parseFloat(hourlyPrice) || 0;
                }
                return acc;
            }, { SN: 0, ENA: 0, CW: 0 });

            setHourlyRates(mappedRates);
            return mappedRates;
        } catch (error) {
            console.error('Error fetching hourly rates:', error);
            const fallback = { SN: 0, ENA: 0, CW: 0 };
            setHourlyRates(fallback);
            return fallback;
        }
    };



    // Function to calculate cost based on hourly rates
    const calculateCost = async (item) => {
        // Get date from carePlan object instead of URL parameter
        const hourlyRates = await fetchHourlyRates(carePlan?.DateCreated || new Date().toISOString().split('T')[0]);

        if (!hourlyRates) {
            return 'Error calculating cost';
        }

        return (
            parseFloat(item.sn_hours_day) * hourlyRates.SN +
            parseFloat(item.sn_hours_night) * hourlyRates.SN +
            parseFloat(item.ena_hours_day) * hourlyRates.ENA +
            parseFloat(item.ena_hours_night) * hourlyRates.ENA +
            parseFloat(item.cw_hours_day) * hourlyRates.CW +
            parseFloat(item.cw_hours_night) * hourlyRates.CW
        ).toFixed(2);
    };





    const handleSaveAsNew = async () => {
        if (!lineItems || lineItems.length === 0) {
            showToast('Please add at least one activity before creating the care plan.', 'warning');
            return;
        }
        setIsSaving(true);
        try {

            // Get today's date and a default end date (1 year from now)
            const today = new Date().toISOString().split('T')[0];
            const nextYear = new Date();
            nextYear.setFullYear(nextYear.getFullYear() + 1);
            const defaultEndDate = nextYear.toISOString().split('T')[0];
            
            // Get customer care type ID from customer details
            const customerCareTypeId = customerDetails?.customer_care_type_id ||
                                       customerDetails?.CustomerCareTypeID ||
                                       lineItems[0]?.customer_care_type_id || 1;
            
            const lineItemsWithCalculatedHours = lineItems.map(item => {
                const multiplier = getMultiplier(item.multiplier);

                let activityId = item.activity_id || item.ActivityID;
                let activityKey = item.activity_key || item.ActivityKey;

                // Only derive activity_id from activity_key when activity_id is not already a valid number.
                // activity_key numbers (e.g. "activity_11") are a sequential key counter, NOT the DB id,
                // so overriding a valid activityId with the key number causes duplicate-key errors.
                if (typeof activityId !== 'number') {
                    if (activityKey && typeof activityKey === 'string' && activityKey.startsWith('legacy_activity_')) {
                        activityId = parseInt(activityKey.replace('legacy_activity_', ''), 10);
                    } else if (activityKey && typeof activityKey === 'string' && activityKey.startsWith('activity_')) {
                        activityId = parseInt(activityKey.replace('activity_', ''), 10);
                    } else if (activityId && !isNaN(Number(activityId))) {
                        activityId = parseInt(activityId, 10);
                    }
                }

                if (!activityKey) {
                    activityKey = `legacy_activity_${activityId}`;
                }

                return {
                    ...item,
                    activity_id: activityId,
                    activity_key: activityKey,
                    activity_name: item.activity_name || item.ActivityName || item.activity_name,
                    customer_care_type_id: String(item.customer_care_type_id || customerCareTypeId),
                    date_from: item.date_from || today,
                    date_to: item.date_to || defaultEndDate,
                    frequency: item.frequency || 1,
                    sn_hours_day: (parseFloat(item.sn_hours_day || 0) * multiplier).toFixed(2),
                    sn_hours_night: (parseFloat(item.sn_hours_night || 0) * multiplier).toFixed(2),
                    ena_hours_day: (parseFloat(item.ena_hours_day || 0) * multiplier).toFixed(2),
                    ena_hours_night: (parseFloat(item.ena_hours_night || 0) * multiplier).toFixed(2),
                    cw_hours_day: (parseFloat(item.cw_hours_day || 0) * multiplier).toFixed(2),
                    cw_hours_night: (parseFloat(item.cw_hours_night || 0) * multiplier).toFixed(2),
                    benefit_body: item.benefit_body || 0,
                    benefit_mind: item.benefit_mind || 0,
                    benefit_social: item.benefit_social || 0,
                };
            });

            // Prefer URL params (new plan flow), fall back to the existing plan's site context
            const payloadTenantId = effectiveTenantId || carePlanTenantSite?.tenant_id;
            const payloadSiteId = urlSiteId || carePlanTenantSite?.site_id;
            const payloadTenantSiteId = carePlanTenantSite?.tenant_site_id;

            // Stamp tenant_site_id on each line item so the backend can JOIN activity metadata.
            // Use the activity's own tenant_site_id first; fall back to the care plan's site.
            // Global/legacy activities (tenant_site_id null) are left as null — the backend
            // cross-references them by activity_id instead.
            const lineItemsWithSite = lineItemsWithCalculatedHours.map(item => ({
                ...item,
                tenant_site_id: item.tenant_site_id || payloadTenantSiteId || null,
            }));

            const payload = {
                customer_id: id,
                date_created: today,
                line_items: lineItemsWithSite,
                ...(payloadTenantId && { tenant_id: payloadTenantId }),
                ...(payloadSiteId && { site_id: payloadSiteId }),
                ...(payloadTenantSiteId && { tenant_site_id: payloadTenantSiteId }),
            };
            
            const response = await fetchWithAuth(`${API_BASE_URL}/createCarePlan`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            
            if (response.ok) {
                const result = await response.json();
                showToast('New Service Plan created successfully');
                navigate(`/careplans/${id}`);
            } else {
                const message = await readApiErrorMessage(
                    response,
                    'We couldn’t create the service plan. Please try again.'
                );
                console.error('Failed Create Response:', message);
                showToast(message, 'danger');
            }
        } catch (error) {
            console.error('An error occurred during save as new:', error);
            showToast('We couldn’t create the service plan. Please check your connection and try again.', 'danger');
        } finally {
            setIsSaving(false);
        }
    };


    const handleSaveEdit = async () => {
        if (isRemovedPlan) {
            showToast('This service plan has been removed, so it can’t be edited.', 'warning');
            return;
        }
        setIsSaving(true);
        try {
            // Apply multiplier to base minutes before sending
            
            const lineItemsWithCalculatedHours = lineItems.map(item => {
                const multiplier = getMultiplier(item.multiplier);

                let activityId = item.activity_id || item.ActivityID;
                let activityKey = item.activity_key || item.ActivityKey;

                // Only derive activity_id from activity_key when activity_id is not already a valid number.
                // activity_key numbers (e.g. "activity_11") are a sequential key counter, NOT the DB id,
                // so overriding a valid activityId with the key number causes duplicate-key errors.
                if (typeof activityId !== 'number') {
                    if (activityKey && typeof activityKey === 'string' && activityKey.startsWith('legacy_activity_')) {
                        activityId = parseInt(activityKey.replace('legacy_activity_', ''), 10);
                    } else if (activityKey && typeof activityKey === 'string' && activityKey.startsWith('activity_')) {
                        activityId = parseInt(activityKey.replace('activity_', ''), 10);
                    } else if (activityId && !isNaN(Number(activityId))) {
                        activityId = parseInt(activityId, 10);
                    }
                }

                if (!activityKey) {
                    activityKey = `legacy_activity_${activityId}`;
                }

                return {
                    ...item,
                    activity_id: activityId,
                    activity_key: activityKey,
                    activity_name: item.activity_name || item.ActivityName || item.activity_name,
                    customer_care_type_id: String(item.customer_care_type_id || item.CustomerCareTypeID || ''),
                    sn_hours_day: (parseFloat(item.sn_hours_day || 0) * multiplier).toFixed(2),
                    sn_hours_night: (parseFloat(item.sn_hours_night || 0) * multiplier).toFixed(2),
                    ena_hours_day: (parseFloat(item.ena_hours_day || 0) * multiplier).toFixed(2),
                    ena_hours_night: (parseFloat(item.ena_hours_night || 0) * multiplier).toFixed(2),
                    cw_hours_day: (parseFloat(item.cw_hours_day || 0) * multiplier).toFixed(2),
                    cw_hours_night: (parseFloat(item.cw_hours_night || 0) * multiplier).toFixed(2),
                };
            });
            
            const editTenantSiteId = carePlanTenantSite?.tenant_site_id;
            const lineItemsWithSite = lineItemsWithCalculatedHours.map(item => ({
                ...item,
                tenant_site_id: item.tenant_site_id || editTenantSiteId || null,
            }));

            const editPayload = {
                customer_id: id,
                care_plan_id: carePlanId || carePlan?.care_plan_id || carePlan?.CarePlanID,
                date_created: carePlan?.date_created || carePlan?.DateCreated || new Date().toISOString().split('T')[0],
                line_items: lineItemsWithSite,
            };
            
            const response = await fetchWithAuth(`${API_BASE_URL}/EditCarePlan`, {
                method: 'POST',
                body: JSON.stringify(editPayload)
            });

            if (response.ok) {
                showToast('Service Plan updated successfully');
                setEdit(false);
                setBulkRemoveActivityIds(new Set());
                if (typeof onPlanSaved === 'function') {
                    onPlanSaved();
                }

                // Refresh the current care plan data
                try {
                    const refreshResponse = await fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${carePlanId}`);
                    if (refreshResponse.ok) {
                        const data = await refreshResponse.json();
                        setCarePlan(data.carePlan);
                        setLineItems(data.lineItems);
                        setFilteredItems(data.lineItems);
                        setTemp(data.lineItems);

                        // Recalculate totals
                        recalculateTotals(data.lineItems);

                        const calculatedBenefitTotals = data.lineItems.reduce(
                            (benefitsTotals, item) => {
                                const m = getMultiplier(item.multiplier);
                                const freq = parseFloat(getFrequencyValue(item)) || 1;
                                const totalHours =
                                    Number(getHoursValue(item, 'sn_hours_day')) +
                                    Number(getHoursValue(item, 'sn_hours_night')) +
                                    Number(getHoursValue(item, 'ena_hours_day')) +
                                    Number(getHoursValue(item, 'ena_hours_night')) +
                                    Number(getHoursValue(item, 'cw_hours_day')) +
                                    Number(getHoursValue(item, 'cw_hours_night'));

                                return {
                                    benefit_body: benefitsTotals.benefit_body + (totalHours * freq * m * ((Number(getBenefitValue(item, 'benefit_body')) / 100) || 0)),
                                    benefit_mind: benefitsTotals.benefit_mind + (totalHours * freq * m * ((Number(getBenefitValue(item, 'benefit_mind')) / 100) || 0)),
                                    benefit_social: benefitsTotals.benefit_social + (totalHours * freq * m * ((Number(getBenefitValue(item, 'benefit_social')) / 100) || 0)),
                                };
                            },
                            { benefit_body: 0, benefit_mind: 0, benefit_social: 0 }
                        );

                        setbenefitsTotals({
                            benefit_body: calculatedBenefitTotals.benefit_body.toFixed(2),
                            benefit_mind: calculatedBenefitTotals.benefit_mind.toFixed(2),
                            benefit_social: calculatedBenefitTotals.benefit_social.toFixed(2),
                        });
                    } else {
                        // Refresh response not OK
                    }
                } catch (err) {
                    console.error('Failed to refresh service plan after edit:', err);
                }
            } else {
                const message = await readApiErrorMessage(
                    response,
                    'We couldn’t update the service plan. Please try again.'
                );
                console.error('Failed to update service plan:', message);
                showToast(message, 'danger');
            }
        } catch (error) {
            console.error('An error occurred during edit save:', error);
            showToast('We couldn’t update the service plan. Please check your connection and try again.', 'danger');
        } finally {
            setIsSaving(false);
        }
    };







    // Add default activities when creating a care plan
    // Auto-populate with default activities based on customer's care type
    useEffect(() => {
        // Only run when creating a new care plan
        if (!creatingCarePlan) return;
        
        // Wait for both customerDetails and activities to be loaded
        if (!customerDetails || !activities || activities.length === 0) {
            // Still initialize with empty items while waiting
            setLineItems([]);
            setFilteredItems([]);
            setTemp([]);
            setTotals({
                sn_hours_day: 0,
                sn_hours_night: 0,
                ena_hours_day: 0,
                ena_hours_night: 0,
                cw_hours_day: 0,
                cw_hours_night: 0,
            });
            setbenefitsTotals({
                benefit_body: 0,
                benefit_mind: 0,
                benefit_social: 0,
            });
            return;
        }
        
        // Get customer's care type
        const customerCareTypeId = customerDetails?.customer_care_type_id;
        
        if (!customerCareTypeId) {
            return;
        }
        
        // Filter activities that match the customer's care type (these are the defaults)
        const defaultActivitiesList = activities.filter(activity => {
            const actCareType = activity.customer_care_type_id;
            if (!actCareType) return false;
            if (Array.isArray(actCareType)) {
                return actCareType.map(String).includes(String(customerCareTypeId));
            }
            return String(actCareType).split(',').map(s => s.trim()).includes(String(customerCareTypeId));
        });
        
        if (defaultActivitiesList.length === 0) {
            return;
        }
        
        // Map activities to line items format
        const defaultLineItems = defaultActivitiesList.map(activity => ({
            ...activity,
            ActivityName: activity.activity_name || activity.ActivityName || activity.name || 'Unknown',
            ActivityID: activity.activity_id || activity.ActivityID,
            activity_id: activity.activity_id || activity.ActivityID,
            activity_name: activity.activity_name || activity.name || '',
            // FIX: Use legacy_activity_ prefix to match DB expectations
            activity_key: activity.activity_key || `legacy_activity_${activity.activity_id || activity.ActivityID}`,
            multiplier: 1,
            frequency: getFrequencyValue(activity) || 1,
            sn_hours_day: parseFloat(getHoursValue(activity, 'sn_hours_day') || 0).toFixed(2),
            sn_hours_night: parseFloat(getHoursValue(activity, 'sn_hours_night') || 0).toFixed(2),
            ena_hours_day: parseFloat(getHoursValue(activity, 'ena_hours_day') || 0).toFixed(2),
            ena_hours_night: parseFloat(getHoursValue(activity, 'ena_hours_night') || 0).toFixed(2),
            cw_hours_day: parseFloat(getHoursValue(activity, 'cw_hours_day') || 0).toFixed(2),
            cw_hours_night: parseFloat(getHoursValue(activity, 'cw_hours_night') || 0).toFixed(2),
            benefit_body: getBenefitValue(activity, 'benefit_body'),
            benefit_mind: getBenefitValue(activity, 'benefit_mind'),
            benefit_social: getBenefitValue(activity, 'benefit_social'),
            customer_care_type_id: activity.customer_care_type_id,
            general_category: activity.general_category,
        }));
        
        // Set the default line items
        setLineItems(defaultLineItems);
        setFilteredItems(defaultLineItems);
        setTemp(defaultLineItems);
        
        // Calculate totals
        recalculateTotals(defaultLineItems);
        
        // Calculate benefit totals
        const calculatedBenefitTotals = defaultLineItems.reduce(
            (benefitsTotals, item) => {
                const multiplier = getMultiplier(item.multiplier);
                const frequency = parseFloat(getFrequencyValue(item)) || 1;
                const totalHours =
                    Number(getHoursValue(item, 'sn_hours_day')) +
                    Number(getHoursValue(item, 'sn_hours_night')) +
                    Number(getHoursValue(item, 'ena_hours_day')) +
                    Number(getHoursValue(item, 'ena_hours_night')) +
                    Number(getHoursValue(item, 'cw_hours_day')) +
                    Number(getHoursValue(item, 'cw_hours_night'));
                
                return {
                    benefit_body: benefitsTotals.benefit_body + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_body')) / 100) || 0)),
                    benefit_mind: benefitsTotals.benefit_mind + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_mind')) / 100) || 0)),
                    benefit_social: benefitsTotals.benefit_social + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_social')) / 100) || 0)),
                };
            },
            {
                benefit_body: 0,
                benefit_mind: 0,
                benefit_social: 0,
            }
        );
        
        setbenefitsTotals({
            benefit_body: calculatedBenefitTotals.benefit_body.toFixed(2),
            benefit_mind: calculatedBenefitTotals.benefit_mind.toFixed(2),
            benefit_social: calculatedBenefitTotals.benefit_social.toFixed(2),
        });
        
    }, [creatingCarePlan, customerDetails, activities]);

    const getMultiplier = (multiplier) => resolveMultiplier(multiplier);

    // Helper function to safely get multiplier for calculations
    const safeGetMultiplier = (multiplier) => resolveMultiplier(multiplier);

    const resolveActivityIndex = (item, actualIndex) => {
        if (actualIndex != null && actualIndex >= 0) return actualIndex;
        return lineItems.findIndex((li) => li.activity_id === item.activity_id);
    };

    const renderMultiplierCell = (item, actualIndex) => {
        const multiplierIsZero = isZeroPrice(item.multiplier);
        const zeroMsg = getZeroMultiplierMessage({ context: 'careplan' });
        let multiplierControl;
        if (!edit) {
            multiplierControl = (
                <span className="pqd-multiplier-readout">{`${item.multiplier != null ? item.multiplier : '1'}`}</span>
            );
        } else if (!isAdmin && isDefaultActivity(item)) {
            multiplierControl = (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`admin-mult-tip-${item.activity_id || actualIndex}`}>{ADMIN_ONLY_MULTIPLIER_TIP}</Tooltip>}
                >
                    <span className="d-inline-block" style={{ cursor: 'not-allowed' }}>
                        <Form.Control
                            type="number"
                            step="0.1"
                            disabled
                            readOnly
                            value={item.multiplier === '' ? '' : (item.multiplier != null ? item.multiplier : '1')}
                            style={{ pointerEvents: 'none', opacity: 0.7 }}
                        />
                    </span>
                </OverlayTrigger>
            );
        } else {
            multiplierControl = (
                <Form.Control
                    type="number"
                    step="0.1"
                    value={item.multiplier === '' ? '' : (item.multiplier != null ? item.multiplier : '1')}
                    onChange={(e) => {
                        const itemIndex = resolveActivityIndex(item, actualIndex);
                        if (itemIndex >= 0) handleMultiplierChange(itemIndex, e.target.value);
                    }}
                    onBlur={(e) => {
                        const itemIndex = resolveActivityIndex(item, actualIndex);
                        if (itemIndex >= 0) handleMultiplierBlur(itemIndex, e.target.value);
                    }}
                    onWheel={(e) => { e.currentTarget.blur(); e.preventDefault(); }}
                />
            );
        }
        return (
            <div className="zero-price-cell">
                {multiplierControl}
                {multiplierIsZero && (
                    <span
                        className="zero-price-chip zero-price-chip--strong"
                        title={zeroMsg}
                        aria-label={zeroMsg}
                    >
                        ×0
                    </span>
                )}
            </div>
        );
    };

    // Helper to get property value - handles both snake_case (API) and PascalCase (code)
    // Also checks metadata.catalogue.defaults for default values
    const getBenefitValue = (item, benefitType) => {
        // Map PascalCase to snake_case for API data
        const snakeCaseMap = {
            'benefit_body': 'benefit_body',
            'benefit_mind': 'benefit_mind',
            'benefit_social': 'benefit_social'
        };
        // First check top-level properties (if not empty/null/0)
        const topLevelValue = item[benefitType] || item[snakeCaseMap[benefitType]];
        if (topLevelValue !== undefined && topLevelValue !== null && topLevelValue !== '' && topLevelValue !== 0) {
            return topLevelValue;
        }
        // Fall back to metadata.catalogue.defaults
        if (item.metadata?.catalogue?.defaults?.[benefitType] !== undefined) {
            return item.metadata.catalogue.defaults[benefitType];
        }
        return 0;
    };

    // Normalise any activity ID to a plain numeric string for comparison.
    // Handles "legacy_activity_9" → "9", "activity_9" → "9", 9 → "9".
    const normalizeActivityId = (id) => {
        if (id == null) return null;
        const s = String(id);
        if (s.startsWith('legacy_activity_')) return s.slice('legacy_activity_'.length);
        if (s.startsWith('activity_')) return s.slice('activity_'.length);
        return s;
    };

    const MISSING_ACTIVITY_NAMES = new Set(['', 'Unknown Activity', 'Unnamed Activity', 'Unknown', 'NO_NAME']);

    const isMissingCatalogueActivity = (item) => {
        if (!item) return false;
        const name = String(item.ActivityName || item.activity_name || '').trim();
        if (name && !MISSING_ACTIVITY_NAMES.has(name)) return false;
        return item.activity_id != null || !!item.activity_key;
    };

    const renderActivityNameCell = (item, { showDefaultStar = false } = {}) => {
        const missing = isMissingCatalogueActivity(item);
        const idLabel = normalizeActivityId(item.activity_id) || item.activity_key || '?';
        const label = missing
            ? `Activity #${idLabel}`
            : (item.ActivityName || item.activity_name);

        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {showDefaultStar && <span style={{ fontSize: '0.85em', lineHeight: 1 }}>⭐</span>}
                <span style={missing ? { color: '#9ca3af', fontStyle: 'italic' } : undefined}>
                    {label}
                </span>
                {missing && (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id={`missing-activity-${item.care_plan_item_id || item.activity_id}`}>
                                This activity has been deleted from the catalogue and can no longer be found.
                            </Tooltip>
                        }
                    >
                        <span
                            role="img"
                            aria-label="Missing from catalogue"
                            style={{ display: 'inline-flex', color: '#d97706', cursor: 'help', flexShrink: 0 }}
                        >
                            <FaExclamationTriangle size={12} />
                        </span>
                    </OverlayTrigger>
                )}
            </span>
        );
    };

    // Helper to get frequency value - handles both snake_case (API) and PascalCase (code)
    // Also checks metadata.catalogue.defaults for default values
    const getFrequencyValue = (item) => {
        // First check top-level properties (if not empty/null/0)
        if (item.frequency !== undefined && item.frequency !== null && item.frequency !== '' && item.frequency !== 0) {
            return item.frequency;
        }
        // Check metadata directly — this holds the customised value and takes priority over catalogue defaults
        const metaFreqDirect = item.metadata?.frequency;
        if (metaFreqDirect != null && metaFreqDirect !== 0 && metaFreqDirect !== '') {
            return metaFreqDirect;
        }
        // Fall back to metadata.catalogue.defaults (original catalogue value)
        const metaFreq = item.metadata?.catalogue?.defaults?.frequency;
        if (metaFreq != null && metaFreq !== 0) {
            return metaFreq;
        }
        // Default to 1 — frequency of 0 means "not set", not "never perform"
        return 1;
    };

    // Helper to get customer property value - handles both snake_case (API) and PascalCase (code)
    const getCustomerValue = (customer, propName) => {
        const snakeCaseMap = {
            'Name': 'name',
            'Surname': 'surname',
            'CustomerID': 'customer_id',
            'CustomerCareTypeID': 'customer_care_type_id',
            'CareTypeID': 'care_type_id'
        };
        return customer[propName] || customer[snakeCaseMap[propName]] || null;
    };

    // Helper to get care plan property value - handles both snake_case (API) and PascalCase (code)
    const getCarePlanValue = (carePlan, propName) => {
        const snakeCaseMap = {
            'CarePlanID': 'care_plan_id',
            'DateCreated': 'date_created',
            'CustomerID': 'customer_id'
        };
        return carePlan[propName] || carePlan[snakeCaseMap[propName]] || null;
    };

    // Helper to get hours - handles both snake_case (API) and PascalCase (code)
    // Also checks metadata.catalogue.defaults for default values
    const getHoursValue = (item, propName) => {
        const snakeCaseMap = {
            'sn_hours_day': 'sn_hours_day',
            'sn_hours_night': 'sn_hours_night',
            'ena_hours_day': 'ena_hours_day',
            'ena_hours_night': 'ena_hours_night',
            'cw_hours_day': 'cw_hours_day',
            'cw_hours_night': 'cw_hours_night'
        };
        // First check top-level properties (skip null/undefined/0 — fall through to metadata)
        const topLevelValue = item[propName] ?? item[snakeCaseMap[propName]];
        if (topLevelValue != null && topLevelValue !== 0) {
            return topLevelValue;
        }
        // Check metadata directly (mirrors QuotationDetails fallback chain)
        if (item.metadata?.[propName] != null && item.metadata[propName] !== 0) {
            return item.metadata[propName];
        }
        // Fall back to metadata.catalogue.defaults
        if (item.metadata?.catalogue?.defaults?.[propName] != null) {
            return item.metadata.catalogue.defaults[propName];
        }
        return 0;
    };

    const calculateBenefit = (item, benefitType) => {
        const totalHours =
            Number(getHoursValue(item, 'sn_hours_day')) +
            Number(getHoursValue(item, 'sn_hours_night')) +
            Number(getHoursValue(item, 'ena_hours_day')) +
            Number(getHoursValue(item, 'ena_hours_night')) +
            Number(getHoursValue(item, 'cw_hours_day')) +
            Number(getHoursValue(item, 'cw_hours_night'));
        
        return totalHours * getMultiplier(item.multiplier) * (Number(getBenefitValue(item, benefitType)) / 100);
    };

    // Helper function to check if an activity is a default activity
    // An activity is a default if its TOP-LEVEL customer_care_type_id matches the customer's care type
    // Activities with empty/null customer_care_type_id are NOT defaults
    // Updated to handle both string keys ("activity_4") and numeric IDs
    const isDefaultActivity = (activityId) => {
        if (!activityId && activityId !== 0) {
            return false;
        }

        // Handle case where activityId is passed as a full object
        let checkId = activityId;
        let checkKey = null;

        if (typeof activityId === 'object' && activityId !== null) {
            checkId = activityId.activity_id || activityId.ActivityID;
            checkKey = activityId.activity_key || activityId.ActivityKey || null;
        }

        if (!checkId && !checkKey) return false;

        // Prefer the authoritative defaultActivities ID list (same as the picker).
        // Do not fall back to name or care-type alone — two activities can share a name
        // while only one is a recommended default.
        if (defaultActivities.length > 0) {
            return defaultActivities.some((id) =>
                String(id) === String(checkId) ||
                (checkKey != null && String(id) === String(checkKey))
            );
        }

        // Fallback when defaultActivities has not loaded yet: care-type match
        const customerCareTypeId = customerDetails?.customer_care_type_id;
        if (!customerCareTypeId) {
            return false;
        }

        let checkCareType = null;
        if (typeof activityId === 'object' && activityId !== null) {
            checkCareType = activityId.customer_care_type_id;
        }

        if (checkCareType !== null && checkCareType !== undefined && checkCareType !== '') {
            const careTypeIds = String(checkCareType).split(',').map(s => s.trim()).filter(Boolean);
            return careTypeIds.includes(String(customerCareTypeId));
        }

        const metadataCareType = typeof activityId === 'object' && activityId !== null
            ? activityId.metadata?.customer_care_type_id
            : null;
        if (metadataCareType !== null && metadataCareType !== undefined && metadataCareType !== '') {
            const metaCareTypeIds = String(metadataCareType).split(',').map(s => s.trim()).filter(Boolean);
            return metaCareTypeIds.includes(String(customerCareTypeId));
        }

        const checkNorm = normalizeActivityId(checkId);
        const matchesActivityId = (activity) => {
            const aNorm = normalizeActivityId(activity.activity_id ?? activity.ActivityID);
            return aNorm && checkNorm && aNorm === checkNorm;
        };

        let activity = sortedActivities.find(matchesActivityId);
        if (!activity) {
            activity = activities.find(matchesActivityId);
        }

        if (!activity || !activity.customer_care_type_id || activity.customer_care_type_id === '') {
            return false;
        }

        const activityCareTypeIds = String(activity.customer_care_type_id).split(',').map(s => s.trim()).filter(Boolean);
        return activityCareTypeIds.includes(String(customerCareTypeId));
    };

    const renderRemoveActivityButton = (item, actualIndex) => {
        const canUpdate = hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE);
        const lockedAsDefault = isDefaultActivity(item) && !isAdmin;
        const disabled = !canUpdate || lockedAsDefault;
        const tip = lockedAsDefault
            ? ADMIN_ONLY_DEFAULT_REMOVE_TIP
            : (!canUpdate ? 'You need permission to update service plans' : null);
        const button = (
            <Button
                variant="link"
                className="d-inline-block p-0"
                disabled={disabled}
                onClick={() => {
                    if (disabled) return;
                    showConfirm(
                        "Are you sure you want to remove this item?",
                        () => {
                            const itemIndex = actualIndex >= 0 ? actualIndex : lineItems.findIndex(li => li.activity_id === item.activity_id);
                            if (itemIndex >= 0) handleRemoveLineItem(itemIndex);
                        },
                        { title: 'Remove Item', confirmLabel: 'Remove' }
                    );
                }}
                style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                <FaTrash style={{ color: disabled ? '#cccccc' : '#dc3545', fontSize: '1.2rem' }} />
            </Button>
        );
        if (!tip) return button;
        return (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`admin-rm-tip-${item.activity_id || actualIndex}`}>{tip}</Tooltip>}
            >
                <span className="d-inline-block">{button}</span>
            </OverlayTrigger>
        );
    };

    const bulkRemoveIncludesDefaults = (() => {
        if (bulkRemoveActivityIds.size === 0) return false;
        return lineItems.some(item => {
            const actId = String(item.activity_id || item.ActivityID || '');
            return bulkRemoveActivityIds.has(actId) && isDefaultActivity(item);
        });
    })();
    const bulkRemoveLockedForDefaults = !isAdmin && bulkRemoveIncludesDefaults;

    const renderBulkRemoveButton = () => {
        if (bulkRemoveActivityIds.size === 0) return null;
        const button = (
            <Button
                variant="danger"
                disabled={bulkRemoveLockedForDefaults}
                onClick={handleBulkRemoveActivities}
            >
                Remove {bulkRemoveActivityIds.size} Selected
            </Button>
        );
        if (!bulkRemoveLockedForDefaults) return button;
        return (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="admin-bulk-rm-tip">{ADMIN_ONLY_BULK_REMOVE_DEFAULTS_TIP}</Tooltip>}
            >
                <span className="d-inline-block">{button}</span>
            </OverlayTrigger>
        );
    };

    // Helper function to check if an activity is already in the line items.
    // Accepts either a full activity object or a bare activity_id value.
    // Uses exact activity_id / activity_key identity only — do NOT normalise
    // "activity_11" ↔ 11, because activity_key suffixes are sequential counters,
    // not DB ids, and that false-match blocks a different activity with the same name.
    const isActivityAlreadyAdded = (activityOrId) => {
        if (!activityOrId && activityOrId !== 0) return false;

        let checkId = activityOrId;
        let checkKey = null;

        if (typeof activityOrId === 'object' && activityOrId !== null) {
            checkId = activityOrId.activity_id ?? activityOrId.ActivityID;
            checkKey = activityOrId.activity_key || activityOrId.ActivityKey || null;
        }

        const same = (a, b) => a != null && b != null && a !== '' && b !== '' && String(a) === String(b);

        return lineItems.some(item => {
            const itemId = item.activity_id ?? item.ActivityID;
            const itemKey = item.activity_key || item.ActivityKey || null;

            return (
                same(checkKey, itemKey) ||
                same(checkId, itemId) ||
                // Catalogue rows map activity_key → activity_id; line items may store either field
                same(checkId, itemKey) ||
                same(checkKey, itemId)
            );
        });
    };

    // Show loading spinner while initial data is being fetched
    if (isLoading) {
        return (
            <div className="container mt-4 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading service plan data...</p>
            </div>
        );
    }

    if (creatingCarePlan) {
        return (
            <>
                <div className="pqd-page">
                    <h1 className="pqd-title">Create New Service Plan</h1>
                    {(customerDetails || displayTenantName || displaySiteName || urlTenantId || urlSiteId) && (
                        <div className="pqd-meta-grid">
                            {customerDetails && (
                                <div>
                                    <div className="pqd-meta-label">Customer</div>
                                    <div className="pqd-meta-value">{customerDetails.name} {customerDetails.surname}</div>
                                </div>
                            )}
                            {(urlTenantId || urlSiteId || displayTenantName || displaySiteName) && (
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <div className="pqd-meta-label">Site</div>
                                    {displayTenantName || displaySiteName
                                        ? <div className="pqd-meta-value">{displayTenantName}{displayTenantName && displaySiteName && ' / '}{displaySiteName}</div>
                                        : (urlTenantId || urlSiteId)
                                            ? <span style={{ color: 'var(--theme-text-muted)', fontSize: '0.9rem' }}>Loading…</span>
                                            : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '10px 14px', marginTop: '4px' }}>
                                                    <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                                                    <span style={{ color: '#856404', fontSize: '0.9rem', flex: 1 }}>
                                                        This service plan is not linked to a tenant or site.
                                                    </span>
                                                </div>
                                            )}
                                </div>
                            )}
                        </div>
                    )}
                    <p className="pqd-subtitle">Select activities below to build the service plan, then click <strong>Create Care Plan and Submit</strong>.</p>
                    <div className="pqd-card">
                        <div className="pqd-card-header">
                            <h2>Overview</h2>
                        </div>
                        <div className="pqd-summary-grid">
                            {planHoursSummaryTable}
                            {quotationHoursBlock}
                            {quotationHoursBlock ? (
                                <>
                                    {labourDifferenceBlock}
                                    <div className="pqd-summary-grid-span">
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr>
                                                    <th colSpan="2" style={{ textAlign: 'center' }}>
                                                        <strong>Summary of Benefits</strong>
                                                    </th>
                                                </tr>
                                                <tr>
                                                    <th>Benefit Type</th>
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lineItems.map((item, index) => (
                                                    <tr key={index}>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td>Body</td>
                                                    <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_body'), 0).toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td>Mind</td>
                                                    <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_mind'), 0).toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td>Social</td>
                                                    <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_social'), 0).toFixed(2)}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            ) : (
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th colSpan="2" style={{ textAlign: 'center' }}>
                                                <strong>Summary of Benefits</strong>
                                            </th>
                                        </tr>
                                        <tr>
                                            <th>Benefit Type</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lineItems.map((item, index) => (
                                            <tr key={index}>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td>Body</td>
                                            <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_body'), 0).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td>Mind</td>
                                            <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_mind'), 0).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td>Social</td>
                                            <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_social'), 0).toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    </div>
                    <div className="pqd-card">
                    <div className="pqd-section-intro">
                        <h3>Care Plan</h3>
                        {isRemovedPlan && (
                            <div className="alert alert-warning py-2 mt-2 mb-0" role="alert">
                                This service plan has been removed and is view-only. Edits and new quotations are not allowed.
                            </div>
                        )}
                    </div>
                    {!edit ? null : (
                        <div className="mb-4 p-3 border rounded" style={{ backgroundColor: 'var(--theme-surface-muted)' }}>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                <h4 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--theme-text)' }}>
                                    Add Activity to Care Plan
                                </h4>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <Button disabled={selectedActivities.length === 0} onClick={handleAddLineItem}>
                                        Add {selectedActivities.length > 1 ? `${selectedActivities.length} Activities` : 'Activity'}
                                    </Button>
                                    {renderBulkRemoveButton()}
                                </div>
                            </div>
                            <SearchableCheckboxPicker
                                label="Select Activities"
                                searchPlaceholder="Search activities..."
                                items={sortedActivities}
                                getItemId={(a) => a.activity_id}
                                getItemLabel={(a) => a.activity_name}
                                isDefaultItem={(a) => defaultActivities.some((id) => String(id) === String(a.activity_id))}
                                isAlreadyAdded={isActivityAlreadyAdded}
                                selectedItems={selectedActivities}
                                onSelectionChange={setSelectedActivities}
                                defaultSectionTitle="Default Activities"
                                allSectionTitle="All Activities"
                                selectedNoun="activity"
                            />
                        </div>
                    )}

                    <div className="pqd-toolbar">
                    <Form.Control
                        type="text"
                        placeholder="Search by Activity Name..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-bar"
                    />
                    <div className='table-controls'>
                        <Button
                            variant="link"
                            className='btn-accent'
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            {collapsed ? <FaAngleRight /> : <FaAngleLeft />}
                            {collapsed ? "Expand Columns" : "Collapse Columns"}
                        </Button>
                    </div>
                    </div>
                    
                    {(() => {
                        const zeroMultiplierBanner = summarizeZeroMultiplierWarnings(filteredItems);
                        return zeroMultiplierBanner ? (
                            <div className="zero-price-warning-banner" role="status">
                                <span className="zero-price-warning-banner__icon" aria-hidden="true">!</span>
                                <div className="zero-price-warning-banner__body">
                                    <span className="zero-price-warning-banner__title">Zero multipliers</span>
                                    {zeroMultiplierBanner}
                                </div>
                            </div>
                        ) : null;
                    })()}

                    {/* Default Activities Table */}
                    {/* Show this section if there are line items that are default activities - we check this even if defaultActivities array isn't populated yet */}
                    {filteredItems.filter(item => isDefaultActivity(item)).length > 0 && (
                        <div style={{ marginBottom: '30px' }}>
                            <p style={{ marginBottom: '8px', fontSize: '0.8rem', fontWeight: '600', color: '#28a745', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Default Activities</p>
                            <div className='table-wrapper'>
                                <Table striped bordered hover className="careplan-aligned-table">
                                    {renderActivitiesColGroup(edit)}
                                    <thead>
                                        <tr>
                                            {edit && <th style={{ paddingLeft: '20px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                                    <span>Actions</span>
                                                    <button type="button" className="btn btn-link p-0 text-decoration-none" style={{ fontSize: '0.75rem', lineHeight: 1 }}
                                                        onClick={() => {
                                                            const groupIds = filteredItems.filter(item => isDefaultActivity(item)).map(item => String(item.activity_id || item.ActivityID || '')).filter(Boolean);
                                                            const allSelected = groupIds.length > 0 && groupIds.every(id => bulkRemoveActivityIds.has(id));
                                                            const next = new Set(bulkRemoveActivityIds);
                                                            if (allSelected) { groupIds.forEach(id => next.delete(id)); } else { groupIds.forEach(id => next.add(id)); }
                                                            setBulkRemoveActivityIds(next);
                                                        }}
                                                    >
                                                        {(() => { const ids = filteredItems.filter(item => isDefaultActivity(item)).map(i => String(i.activity_id || i.ActivityID || '')).filter(Boolean); return ids.length > 0 && ids.every(id => bulkRemoveActivityIds.has(id)) ? 'Deselect All' : 'Select All'; })()}
                                                    </button>
                                                </div>
                                            </th>}
                                            <th></th>
                                            <th></th>
                                            <th></th>
                                            <th colSpan={2} onClick={() => handleSort('sn_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Minutes" />{getSortIndicator('sn_hours_day')}</th>
                                            <th colSpan={2} onClick={() => handleSort('ena_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Minutes" />{getSortIndicator('ena_hours_day')}</th>
                                            <th colSpan={2} onClick={() => handleSort('cw_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Minutes" />{getSortIndicator('cw_hours_day')}</th>
                                            <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Body {getSortIndicator('benefit_body')}</th>
                                            <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Mind {getSortIndicator('benefit_body')}</th>
                                            <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Social {getSortIndicator('benefit_body')}</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell' }}></th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell' }}></th>
                                        </tr>
                                    </thead>
                                    <thead>
                                        <tr>
                                            {edit && <th style={{ top: 39, textAlign: 'center' }}></th>}
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ActivityName')}>Activity {getSortIndicator('ActivityName')}</th>
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('Multiplier', true)}>Multiplier {getSortIndicator('Multiplier')}</th>
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('Frequency')}>Frequency {getSortIndicator('Frequency')}</th>
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('sn_hours_day', true)}>Day {getSortIndicator('sn_hours_day')}</th>
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('sn_hours_night', true)}>Night{getSortIndicator('sn_hours_night')}</th>
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ena_hours_day', true)}>Day{getSortIndicator('ena_hours_day')}</th>
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ena_hours_night', true)}>Night{getSortIndicator('ena_hours_night')}</th>
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('cw_hours_day', true)}>Day{getSortIndicator('cw_hours_day')}</th>
                                            <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('cw_hours_night', true)}>Night{getSortIndicator('cw_hours_night')}</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>%{getSortIndicator('benefit_body')}</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_mind', true)}>%{getSortIndicator('benefit_mind')}</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_social', true)}>%{getSortIndicator('benefit_social')}</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('CustomerCareTypeID')}>Care Type {getSortIndicator('CustomerCareTypeID')}</th>
                                            <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('GeneralCategory')}>General Category {getSortIndicator('GeneralCategory')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredItems
                                            .filter(item => isDefaultActivity(item))
                                            .map((item, index) => {
                                                const actualIndex = lineItems.findIndex(li => li.activity_id === item.activity_id);
                                                const actId = String(item.activity_id || item.ActivityID || '');
                                                return (
                                                    <tr
                                                        key={index}
                                                        className={isZeroPrice(item.multiplier) ? 'zero-price-row-flag' : undefined}
                                                        style={{
                                                        backgroundColor: bulkRemoveActivityIds.has(actId) ? '#ffe5e5' : '#f8fff8',
                                                        borderLeft: '4px solid #28a745',
                                                        position: 'relative'
                                                    }}>
                                                        {edit && (
                                                            <td style={{ whiteSpace: 'nowrap', paddingLeft: '20px', paddingRight: '12px', verticalAlign: 'middle' }}>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    className="d-inline-block me-2"
                                                                    checked={bulkRemoveActivityIds.has(actId)}
                                                                    onChange={(e) => {
                                                                        const newSet = new Set(bulkRemoveActivityIds);
                                                                        e.target.checked ? newSet.add(actId) : newSet.delete(actId);
                                                                        setBulkRemoveActivityIds(newSet);
                                                                    }}
                                                                />
                                                                {renderRemoveActivityButton(item, actualIndex)}
                                                            </td>
                                                        )}
                                                        <td style={{ paddingLeft: '12px', verticalAlign: 'middle' }}>
                                                            {renderActivityNameCell(item, { showDefaultStar: true })}
                                                        </td>
                                                        <td>{renderMultiplierCell(item, actualIndex)}</td>
                                                        <td>{getFrequencyValue(item)}</td>
                                                        <td>{((getHoursValue(item, 'sn_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                        <td>{((getHoursValue(item, 'sn_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                        <td>{((getHoursValue(item, 'ena_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                        <td>{((getHoursValue(item, 'ena_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                        <td>{((getHoursValue(item, 'cw_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                        <td>{((getHoursValue(item, 'cw_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                        <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_body') || 0)}</td>
                                                        <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_body').toFixed(2)}</td>
                                                        <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_mind') || 0)}</td>
                                                        <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_mind').toFixed(2)}</td>
                                                        <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_social') || 0)}</td>
                                                        <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_social').toFixed(2)}</td>
                                                        <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{item.customer_care_type_id}</td>
                                                        <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{item.general_category}</td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Regular Activities Table */}
                    <div className='table-wrapper'>
                        <Table striped bordered hover className="careplan-aligned-table">
                            {renderActivitiesColGroup(edit)}
                            <thead>
                                <tr>
                                    {edit && <th style={{ paddingLeft: '20px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                            <span>Actions</span>
                                            <button type="button" className="btn btn-link p-0 text-decoration-none" style={{ fontSize: '0.75rem', lineHeight: 1 }}
                                                onClick={() => {
                                                    const groupIds = filteredItems.filter(item => !isDefaultActivity(item)).map(item => String(item.activity_id || item.ActivityID || '')).filter(Boolean);
                                                    const allSelected = groupIds.length > 0 && groupIds.every(id => bulkRemoveActivityIds.has(id));
                                                    const next = new Set(bulkRemoveActivityIds);
                                                    if (allSelected) { groupIds.forEach(id => next.delete(id)); } else { groupIds.forEach(id => next.add(id)); }
                                                    setBulkRemoveActivityIds(next);
                                                }}
                                            >
                                                {(() => { const ids = filteredItems.filter(item => !isDefaultActivity(item)).map(i => String(i.activity_id || i.ActivityID || '')).filter(Boolean); return ids.length > 0 && ids.every(id => bulkRemoveActivityIds.has(id)) ? 'Deselect All' : 'Select All'; })()}
                                            </button>
                                        </div>
                                    </th>}
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th colSpan={2} onClick={() => handleSort('sn_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Minutes" />{getSortIndicator('sn_hours_day')}</th>
                                    <th colSpan={2} onClick={() => handleSort('ena_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Minutes" />{getSortIndicator('ena_hours_day')}</th>
                                    <th colSpan={2} onClick={() => handleSort('cw_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Minutes" />{getSortIndicator('cw_hours_day')}</th>
                                    <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Body {getSortIndicator('benefit_body')}</th>
                                    <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Mind {getSortIndicator('benefit_body')}</th>
                                    <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Social {getSortIndicator('benefit_body')}</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell' }}></th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell' }}></th>
                                </tr>
                            </thead>
                            <thead>
                                <tr>
                                    {edit && <th style={{ top: 39, textAlign: 'center' }}></th>}
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ActivityName')}>Activity {getSortIndicator('ActivityName')}</th>
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('Multiplier', true)}>Multiplier {getSortIndicator('Multiplier')}</th>
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('Frequency')}>Frequency {getSortIndicator('Frequency')}</th>
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('sn_hours_day', true)}>Day {getSortIndicator('sn_hours_day')}</th>
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('sn_hours_night', true)}>Night{getSortIndicator('sn_hours_night')}</th>
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ena_hours_day', true)}>Day{getSortIndicator('ena_hours_day')}</th>
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ena_hours_night', true)}>Night{getSortIndicator('ena_hours_night')}</th>
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('cw_hours_day', true)}>Day{getSortIndicator('cw_hours_day')}</th>
                                    <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('cw_hours_night', true)}>Night{getSortIndicator('cw_hours_night')}</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>%{getSortIndicator('benefit_body')}</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_mind', true)}>%{getSortIndicator('benefit_mind')}</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_social', true)}>%{getSortIndicator('benefit_social')}</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('CustomerCareTypeID')}>Care Type {getSortIndicator('CustomerCareTypeID')}</th>
                                    <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('GeneralCategory')}>General Category {getSortIndicator('GeneralCategory')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems
                                    .filter(item => !isDefaultActivity(item))
                                    .map((item, index) => {
                                        const actualIndex = lineItems.findIndex(li => li.activity_id === item.activity_id && JSON.stringify(li) === JSON.stringify(item));
                                        const actId = String(item.activity_id || item.ActivityID || '');
                                        return (
                                            <tr
                                                key={index}
                                                className={isZeroPrice(item.multiplier) ? 'zero-price-row-flag' : undefined}
                                                style={bulkRemoveActivityIds.has(actId) ? { backgroundColor: '#ffe5e5' } : {}}
                                            >
                                                {edit && (
                                                    <td style={{ whiteSpace: 'nowrap', paddingLeft: '20px', paddingRight: '12px', verticalAlign: 'middle' }}>
                                                        <Form.Check
                                                            type="checkbox"
                                                            className="d-inline-block me-2"
                                                            checked={bulkRemoveActivityIds.has(actId)}
                                                            onChange={(e) => {
                                                                const newSet = new Set(bulkRemoveActivityIds);
                                                                e.target.checked ? newSet.add(actId) : newSet.delete(actId);
                                                                setBulkRemoveActivityIds(newSet);
                                                            }}
                                                        />
                                                        {renderRemoveActivityButton(item, actualIndex)}
                                                    </td>
                                                )}
                                                <td style={{ paddingLeft: '12px', verticalAlign: 'middle' }}>
                                                    {renderActivityNameCell(item)}
                                                </td>
                                                <td>{renderMultiplierCell(item, actualIndex)}</td>
                                                <td>{getFrequencyValue(item)}</td>
                                                <td>{((getHoursValue(item, 'sn_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                <td>{((getHoursValue(item, 'sn_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                <td>{((getHoursValue(item, 'ena_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                <td>{((getHoursValue(item, 'ena_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                <td>{((getHoursValue(item, 'cw_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                <td>{((getHoursValue(item, 'cw_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_body') || 0)}</td>
                                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_body').toFixed(2)}</td>
                                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_mind') || 0)}</td>
                                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_mind').toFixed(2)}</td>
                                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_social') || 0)}</td>
                                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_social').toFixed(2)}</td>
                                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{item.customer_care_type_id}</td>
                                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{item.general_category}</td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </Table>
                    </div>


                    <div className="pqd-actions">
                        {edit ? (
                            <>
                                {/* Restore original line items and recalculate totals on cancel */}
                                <Button variant="secondary" onClick={() => {
                                    showConfirm(
                                        "Are you sure you want to cancel? Any unsaved changes will be lost.",
                                        () => {
                                            setEdit(false);
                                            setBulkRemoveActivityIds(new Set());
                                            const fetchOriginalCarePlan = async () => {
                                                try {
                                                    const response = await fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${carePlanId}`);
                                                    if (response.ok) {
                                                        const data = await response.json();
                                                        setCarePlan(data.carePlan);
                                                        setLineItems(data.lineItems);
                                                        setFilteredItems(data.lineItems);
                                                        setTemp(data.lineItems);

                                                        recalculateTotals(data.lineItems);

                                                        const calculatedBenefitTotals = data.lineItems.reduce(
                                                            (benefitsTotals, item) => {
                                                                const multiplier = getMultiplier(item.multiplier);
                                                                const frequency = parseFloat(getFrequencyValue(item)) || 1;
                                                                const totalHours =
                                                                    Number(getHoursValue(item, 'sn_hours_day')) +
                                                                    Number(getHoursValue(item, 'sn_hours_night')) +
                                                                    Number(getHoursValue(item, 'ena_hours_day')) +
                                                                    Number(getHoursValue(item, 'ena_hours_night')) +
                                                                    Number(getHoursValue(item, 'cw_hours_day')) +
                                                                    Number(getHoursValue(item, 'cw_hours_night'));
                                                                return {
                                                                    benefit_body: benefitsTotals.benefit_body + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_body')) / 100) || 0)),
                                                                    benefit_mind: benefitsTotals.benefit_mind + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_mind')) / 100) || 0)),
                                                                    benefit_social: benefitsTotals.benefit_social + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_social')) / 100) || 0)),
                                                                };
                                                            },
                                                            { benefit_body: 0, benefit_mind: 0, benefit_social: 0 }
                                                        );
                                                        setbenefitsTotals({
                                                            benefit_body: calculatedBenefitTotals.benefit_body.toFixed(2),
                                                            benefit_mind: calculatedBenefitTotals.benefit_mind.toFixed(2),
                                                            benefit_social: calculatedBenefitTotals.benefit_social.toFixed(2),
                                                        });
                                                    } else {
                                                        console.error('Failed to fetch original service plan data');
                                                    }
                                                } catch (error) {
                                                    console.error('An error occurred while fetching the original service plan:', error);
                                                }
                                            };
                                            fetchOriginalCarePlan();
                                        },
                                        { title: 'Cancel Changes', confirmLabel: 'Discard Changes', confirmVariant: 'warning' }
                                    );
                                }} className="me-2">Cancel</Button>
                                <Button disabled={!carePlan || isSaving} variant="primary" onClick={handleSaveEdit} className="me-2">
                                    {isSaving ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</> : 'Save Changes'}
                                </Button>
                                <Button disabled={isSaving} variant="outline-primary" onClick={handleSaveAsNew}>
                                    {isSaving ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</> : 'Save as New Plan'}
                                </Button>
                            </>
                        ) : (
                            <>
                                {/* Store current items in temp state when entering edit mode */}
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                        isRemovedPlan
                                            ? <Tooltip>Removed service plans cannot be edited.</Tooltip>
                                            : <></>
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                if (!isRemovedPlan) {
                                                    setEdit(true);
                                                    setTemp([...lineItems]);
                                                }
                                            }}
                                            disabled={isRemovedPlan}
                                            className="me-2"
                                            style={{
                                                cursor: isRemovedPlan ? 'not-allowed' : 'pointer',
                                                opacity: isRemovedPlan ? 0.5 : 1
                                            }}
                                        >
                                            Edit Care Plan
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                        isRemovedPlan
                                            ? <Tooltip>Cannot create a quotation for a removed service plan.</Tooltip>
                                            : !carePlan
                                            ? <Tooltip>A quote can only be created on a care plan that has already been saved.</Tooltip>
                                            : !hasPermission(PERMISSIONS.QUOTATION_CREATE)
                                                ? <Tooltip>You do not have permission to create quotations.</Tooltip>
                                                : <></>
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="success"
                                            onClick={() => {
                                                if (!isRemovedPlan && hasPermission(PERMISSIONS.QUOTATION_CREATE) && carePlan) {
                                                    handleCreateQuoteClick();
                                                }
                                            }}
                                            disabled={creatingQuote || isRemovedPlan || !hasPermission(PERMISSIONS.QUOTATION_CREATE) || !carePlan}
                                            style={{
                                                cursor: (!isRemovedPlan && hasPermission(PERMISSIONS.QUOTATION_CREATE) && carePlan) ? 'pointer' : 'not-allowed',
                                                opacity: (!isRemovedPlan && hasPermission(PERMISSIONS.QUOTATION_CREATE) && carePlan) ? 1 : 0.5,
                                                pointerEvents: (isRemovedPlan || !hasPermission(PERMISSIONS.QUOTATION_CREATE) || !carePlan) ? 'none' : 'auto'
                                            }}
                                        >
                                            {creatingQuote ? 'Creating Quote...' : 'Create Quote'}
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            </>
                        )}
                    </div>
                    <div className="pqd-actions">
                        <Button disabled={isSaving} onClick={handleSaveAsNew} style={{ minWidth: '200px' }}>
                            {isSaving ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</> : 'Create Care Plan and Submit'}
                        </Button>
                    </div>
                    </div>
                </div>

            <Modal show={confirmModal.show} onHide={() => setConfirmModal(m => ({ ...m, show: false }))} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirmModal.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{confirmModal.message}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmModal(m => ({ ...m, show: false }))}>
                        Cancel
                    </Button>
                    <Button
                        variant={confirmModal.confirmVariant}
                        onClick={() => {
                            if (confirmModal.onConfirm) confirmModal.onConfirm();
                            setConfirmModal(m => ({ ...m, show: false }));
                        }}
                    >
                        {confirmModal.confirmLabel}
                    </Button>
                </Modal.Footer>
            </Modal>

            {toast.show && <div className={`app-toast app-toast-${toast.variant}`}>{toast.message}</div>}
            </>
        );
    }

    if (noActivities) {
        return (
            <div>
                <div className="container mt-4">
                    <h1>No Activities Available</h1>
                    <p>There are no activities available to add to the service plan at the moment.</p>
                </div>
            </div>
        );
    }

    // This check should rarely be reached since isLoading handles the loading state,
    // but keeping as a fallback for any edge cases
    if (!carePlan && !creatingCarePlan) {
        return (
        <div>
            <Navbar bg="primary" variant="dark">
                <Navbar.Brand>Service Plan</Navbar.Brand>
                <Nav className="ml-auto">
                    <Dropdown>
                        <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                            Menu
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item href="/">Home</Dropdown.Item>
                            <Dropdown.Item href="/care-recipients">Customers</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Nav>
            </Navbar>
            <div className="container mt-4 text-center">
                <p>Unable to load service plan data.</p>
            </div>
        </div>
        );
    }

    return (
        <div>
            <div className="pqd-page">
                <h1 className="pqd-title">Service Plan Details</h1>
                <div className="pqd-meta-grid">
                    <div>
                        <div className="pqd-meta-label">Recipient</div>
                        <div className="pqd-meta-value">{customerDetails ? `${customerDetails.name} ${customerDetails.surname}` : '—'}</div>
                    </div>
                    <div>
                        <div className="pqd-meta-label">Care Type</div>
                        <div className="pqd-meta-value">{(() => {
                            const careTypeId = customerDetails?.customer_care_type_id || customerDetails?.CustomerCareTypeID;
                            if (careTypeId && careTypes.length > 0) {
                                const found = careTypes.find(ct =>
                                    Number(ct.CareTypeID || ct.care_type_id || ct.id) === Number(careTypeId)
                                );
                                return found ? (found.Name || found.name || found.care_type_name || `ID: ${careTypeId}`) : careTypeId;
                            }
                            return careTypeId || '—';
                        })()}</div>
                    </div>
                    <div>
                        <div className="pqd-meta-label">Service Plan ID</div>
                        <div className="pqd-meta-value">{formatCarePlanId(carePlanId || carePlan?.care_plan_id) || '—'}</div>
                    </div>
                    <div>
                        <div className="pqd-meta-label">Active Quotation</div>
                        <div className="pqd-meta-value">{activeQuote?.quotation_id ? (
                            edit ? (
                                <span className="pqd-meta-link" onClick={() => showToast('Please save your changes before continuing.', 'warning')}>
                                    {formatQuotationId(activeQuote.quotation_id)}
                                </span>
                            ) : embedded && typeof onOpenQuote === 'function' ? (
                                <span
                                    className="pqd-meta-link"
                                    onClick={() => onOpenQuote(activeQuote.quotation_id)}
                                >
                                    {formatQuotationId(activeQuote.quotation_id)}
                                </span>
                            ) : (
                                <Link to={`/careplans/${id}/${carePlanId || carePlan?.care_plan_id}/quote/${activeQuote.quotation_id}`}>
                                    {formatQuotationId(activeQuote.quotation_id)}
                                </Link>
                            )
                        ) : <span style={{ color: 'var(--theme-text-muted)' }}>None</span>}</div>
                    </div>
                    <div>
                        <div className="pqd-meta-label">Created</div>
                        <div className="pqd-meta-value">{formatCreatedDate(carePlan?.date_created || carePlan?.DateCreated)}</div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div className="pqd-meta-label">Site</div>
                        {displayTenantName || displaySiteName
                            ? <div className="pqd-meta-value">{displayTenantName}{displayTenantName && displaySiteName && ' / '}{displaySiteName}</div>
                            : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '10px 14px', marginTop: '4px' }}>
                                    <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                                    <span style={{ color: '#856404', fontSize: '0.9rem', flex: 1 }}>
                                        This service plan is not linked to a tenant or site.
                                    </span>
                                    <button
                                        onClick={handleCreateNewPlan}
                                        style={{ display: 'inline-block', padding: '6px 14px', background: 'var(--theme-accent)', color: '#fff', border: 'none', borderRadius: '5px', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', cursor: 'pointer' }}
                                    >
                                        Create New Plan
                                    </button>
                                </div>
                            )}
                    </div>
                </div>

                <div className="pqd-card">
                    <div className="pqd-card-header">
                        <h2>Overview</h2>
                    </div>
                    <div className="pqd-summary-grid">
                    {planHoursSummaryTable}
                    {quotationHoursBlock}
                    {quotationHoursBlock ? (
                        <>
                            {labourDifferenceBlock}
                            <div className="pqd-summary-grid-span">
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th colSpan="2" style={{ textAlign: 'center' }}>
                                                <strong>Summary of Benefits</strong>
                                            </th>
                                        </tr>
                                        <tr>
                                            <th>Benefit Type</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lineItems.map((item, index) => (
                                            <tr key={index}>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td>Body</td>
                                            <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_body'), 0).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td>Mind</td>
                                            <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_mind'), 0).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td>Social</td>
                                            <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_social'), 0).toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    ) : (
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th colSpan="2" style={{ textAlign: 'center' }}>
                                        <strong>Summary of Benefits</strong>
                                    </th>
                                </tr>
                                <tr>
                                    <th>Benefit Type</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems.map((item, index) => (
                                    <tr key={index}>
                                    </tr>
                                ))}
                                <tr>
                                    <td>Body</td>
                                    <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_body'), 0).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td>Mind</td>
                                    <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_mind'), 0).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td>Social</td>
                                    <td>{lineItems.reduce((acc, item) => acc + calculateBenefit(item, 'benefit_social'), 0).toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </Table>
                    )}
                    </div>
                </div>
                <div className="pqd-card">
                <div className="pqd-section-intro">
                    <h3>Service Plan</h3>
                    {isRemovedPlan && (
                        <div className="alert alert-warning py-2 mt-2 mb-0" role="alert">
                            This service plan has been removed and is view-only. Edits and new quotations are not allowed.
                        </div>
                    )}
                </div>
                {!edit ? null : (
                    <div className="mb-4 p-3 border rounded" style={{ backgroundColor: 'var(--theme-surface-muted)' }}>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <h4 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--theme-text)' }}>
                                Add Activity to Service Plan
                            </h4>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <Button
                                    disabled={selectedActivities.length === 0 || !hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE)}
                                    onClick={handleAddLineItem}
                                    style={{
                                        cursor: hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE) ? 'pointer' : 'not-allowed',
                                        opacity: hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE) ? 1 : 0.5
                                    }}
                                >
                                    Add {selectedActivities.length > 1 ? `${selectedActivities.length} Activities` : 'Activity'}
                                </Button>
                                {renderBulkRemoveButton()}
                            </div>
                        </div>
                        <SearchableCheckboxPicker
                            label="Select Activities"
                            searchPlaceholder="Search activities..."
                            items={sortedActivities}
                            getItemId={(a) => a.activity_id}
                            getItemLabel={(a) => a.activity_name}
                            isDefaultItem={(a) => defaultActivities.some((id) => String(id) === String(a.activity_id))}
                            isAlreadyAdded={isActivityAlreadyAdded}
                            selectedItems={selectedActivities}
                            onSelectionChange={setSelectedActivities}
                            defaultSectionTitle="Default Activities"
                            allSectionTitle="All Activities"
                            selectedNoun="activity"
                        />
                    </div>
                )}

                <div className="pqd-toolbar">
                <Form.Control
                    type="text"
                    placeholder="Search by Activity Name..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="search-bar"
                />
                <div className='table-controls'>
                    <Button
                        variant="link"
                        className='btn-accent'
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {collapsed ? <FaAngleRight /> : <FaAngleLeft />}
                        {collapsed ? "Expand Columns" : "Collapse Columns"}
                    </Button>
                </div>
                </div>

                {(() => {
                    const zeroMultiplierBanner = summarizeZeroMultiplierWarnings(filteredItems.length ? filteredItems : lineItems);
                    return zeroMultiplierBanner ? (
                        <div className="zero-price-warning-banner" role="status">
                            <span className="zero-price-warning-banner__icon" aria-hidden="true">!</span>
                            <div className="zero-price-warning-banner__body">
                                <span className="zero-price-warning-banner__title">Zero multipliers</span>
                                {zeroMultiplierBanner}
                            </div>
                        </div>
                    ) : null;
                })()}

                {/* Default Activities Table */}
                {/* Show this section if there are line items that are default activities */}
                {lineItems.length > 0 && lineItems.some(item => isDefaultActivity(item)) && (
                    <div style={{ marginBottom: '30px' }}>
                        <p style={{ marginBottom: '8px', fontSize: '0.8rem', fontWeight: '600', color: '#28a745', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Default Activities</p>
                        <div className='table-wrapper'>
                            <Table striped bordered hover className="careplan-aligned-table">
                                {renderActivitiesColGroup(edit)}
                                <thead>
                                    <tr>
                                        {edit && <th style={{ paddingLeft: '20px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                                <span>Actions</span>
                                                <button type="button" className="btn btn-link p-0 text-decoration-none" style={{ fontSize: '0.75rem', lineHeight: 1 }}
                                                    onClick={() => {
                                                        const groupIds = filteredItems.filter(item => isDefaultActivity(item)).map(item => String(item.activity_id || item.ActivityID || '')).filter(Boolean);
                                                        const allSelected = groupIds.length > 0 && groupIds.every(id => bulkRemoveActivityIds.has(id));
                                                        const next = new Set(bulkRemoveActivityIds);
                                                        if (allSelected) { groupIds.forEach(id => next.delete(id)); } else { groupIds.forEach(id => next.add(id)); }
                                                        setBulkRemoveActivityIds(next);
                                                    }}
                                                >
                                                    {(() => { const ids = filteredItems.filter(item => isDefaultActivity(item)).map(i => String(i.activity_id || i.ActivityID || '')).filter(Boolean); return ids.length > 0 && ids.every(id => bulkRemoveActivityIds.has(id)) ? 'Deselect All' : 'Select All'; })()}
                                                </button>
                                            </div>
                                        </th>}
                                        <th></th>
                                        <th></th>
                                        <th></th>
                                        <th colSpan={2} onClick={() => handleSort('sn_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Minutes" />{getSortIndicator('sn_hours_day')}</th>
                                        <th colSpan={2} onClick={() => handleSort('ena_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Minutes" />{getSortIndicator('ena_hours_day')}</th>
                                        <th colSpan={2} onClick={() => handleSort('cw_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Minutes" />{getSortIndicator('cw_hours_day')}</th>
                                        <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Body {getSortIndicator('benefit_body')}</th>
                                        <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Mind {getSortIndicator('benefit_body')}</th>
                                        <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Social {getSortIndicator('benefit_body')}</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell' }}></th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell' }}></th>
                                    </tr>
                                </thead>
                                <thead>
                                    <tr>
                                        {edit && <th style={{ top: 39, textAlign: 'center' }}></th>}
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ActivityName')}>Activity {getSortIndicator('activity_name')}</th>
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('Multiplier', true)}>Multiplier {getSortIndicator('Multiplier')}</th>
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('Frequency')}>Frequency {getSortIndicator('Frequency')}</th>
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('sn_hours_day', true)}>Day {getSortIndicator('sn_hours_day')}</th>
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('sn_hours_night', true)}>Night{getSortIndicator('sn_hours_night')}</th>
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ena_hours_day', true)}>Day{getSortIndicator('ena_hours_day')}</th>
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ena_hours_night', true)}>Night{getSortIndicator('ena_hours_night')}</th>
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('cw_hours_day', true)}>Day{getSortIndicator('cw_hours_day')}</th>
                                        <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('cw_hours_night', true)}>Night{getSortIndicator('cw_hours_night')}</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>%{getSortIndicator('benefit_body')}</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_mind', true)}>%{getSortIndicator('benefit_mind')}</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_social', true)}>%{getSortIndicator('benefit_social')}</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('CustomerCareTypeID')}>Care Type {getSortIndicator('CustomerCareTypeID')}</th>
                                        <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('GeneralCategory')}>General Category {getSortIndicator('GeneralCategory')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lineItems
                                        .filter(item => isDefaultActivity(item))
                                        .map((item, index) => {
                                            const actualIndex = lineItems.findIndex(li => li.activity_id === item.activity_id);
                                            const actId = String(item.activity_id || item.ActivityID || '');
                                            return (
                                                <tr
                                                    key={index}
                                                    className={isZeroPrice(item.multiplier) ? 'zero-price-row-flag' : undefined}
                                                    style={{
                                                    backgroundColor: bulkRemoveActivityIds.has(actId) ? '#ffe5e5' : '#f8fff8',
                                                    borderLeft: '4px solid #28a745',
                                                    position: 'relative'
                                                }}>
                                                    {edit && (
                                                        <td style={{ whiteSpace: 'nowrap', paddingLeft: '20px', paddingRight: '12px', verticalAlign: 'middle' }}>
                                                            <Form.Check
                                                                type="checkbox"
                                                                className="d-inline-block me-2"
                                                                checked={bulkRemoveActivityIds.has(actId)}
                                                                onChange={(e) => {
                                                                    const newSet = new Set(bulkRemoveActivityIds);
                                                                    e.target.checked ? newSet.add(actId) : newSet.delete(actId);
                                                                    setBulkRemoveActivityIds(newSet);
                                                                }}
                                                            />
                                                            {renderRemoveActivityButton(item, actualIndex)}
                                                        </td>
                                                    )}
                                                    <td style={{ paddingLeft: '12px', verticalAlign: 'middle' }}>
                                                        {renderActivityNameCell(item, { showDefaultStar: true })}
                                                    </td>
                                                    <td>{renderMultiplierCell(item, actualIndex)}</td>
                                                    <td>{getFrequencyValue(item)}</td>
                                                    <td>{((getHoursValue(item, 'sn_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                    <td>{((getHoursValue(item, 'sn_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                    <td>{((getHoursValue(item, 'ena_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                    <td>{((getHoursValue(item, 'ena_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                    <td>{((getHoursValue(item, 'cw_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                    <td>{((getHoursValue(item, 'cw_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                                    <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_body') || 0)}</td>
                                                    <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_body').toFixed(2)}</td>
                                                    <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_mind') || 0)}</td>
                                                    <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_mind').toFixed(2)}</td>
                                                    <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_social') || 0)}</td>
                                                    <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_social').toFixed(2)}</td>
                                                    <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{item.customer_care_type_id}</td>
                                                    <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{item.general_category}</td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}

                {/* Regular Activities Table */}
                <div className='table-wrapper'>
                    <Table striped bordered hover className="careplan-aligned-table">
                        {renderActivitiesColGroup(edit)}
                        <thead>
                            <tr>
                                {edit && <th style={{ paddingLeft: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                        <span>Actions</span>
                                        <button type="button" className="btn btn-link p-0 text-decoration-none" style={{ fontSize: '0.75rem', lineHeight: 1 }}
                                            onClick={() => {
                                                const groupIds = filteredItems.filter(item => !isDefaultActivity(item)).map(item => String(item.activity_id || item.ActivityID || '')).filter(Boolean);
                                                const allSelected = groupIds.length > 0 && groupIds.every(id => bulkRemoveActivityIds.has(id));
                                                const next = new Set(bulkRemoveActivityIds);
                                                if (allSelected) { groupIds.forEach(id => next.delete(id)); } else { groupIds.forEach(id => next.add(id)); }
                                                setBulkRemoveActivityIds(next);
                                            }}
                                        >
                                            {(() => { const ids = filteredItems.filter(item => !isDefaultActivity(item)).map(i => String(i.activity_id || i.ActivityID || '')).filter(Boolean); return ids.length > 0 && ids.every(id => bulkRemoveActivityIds.has(id)) ? 'Deselect All' : 'Select All'; })()}
                                        </button>
                                    </div>
                                </th>}
                                <th></th>
                                <th></th>
                                <th></th>
                                <th colSpan={2} onClick={() => handleSort('sn_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Minutes" />{getSortIndicator('sn_hours_day')}</th>
                                <th colSpan={2} onClick={() => handleSort('ena_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Minutes" />{getSortIndicator('ena_hours_day')}</th>
                                <th colSpan={2} onClick={() => handleSort('cw_hours_day', true)} style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Minutes" />{getSortIndicator('cw_hours_day')}</th>
                                <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Body {getSortIndicator('benefit_body')}</th>
                                <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Mind {getSortIndicator('benefit_body')}</th>
                                <th colSpan={2} style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>Benefit Social {getSortIndicator('benefit_body')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell' }}></th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell' }}></th>
                            </tr>
                        </thead>
                        <thead>
                            <tr>
                                {edit && <th style={{ top: 39, textAlign: 'center' }}></th>}
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ActivityName')}>Activity {getSortIndicator('ActivityName')}</th>
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('Multiplier', true)}>Multiplier {getSortIndicator('Multiplier')}</th>
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('Frequency')}>Frequency {getSortIndicator('Frequency')}</th>
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('sn_hours_day', true)}>Day {getSortIndicator('sn_hours_day')}</th>
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('sn_hours_night', true)}>Night{getSortIndicator('sn_hours_night')}</th>
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ena_hours_day', true)}>Day{getSortIndicator('ena_hours_day')}</th>
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('ena_hours_night', true)}>Night{getSortIndicator('ena_hours_night')}</th>
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('cw_hours_day', true)}>Day{getSortIndicator('cw_hours_day')}</th>
                                <th style={{ top: 39, textAlign: 'center' }} onClick={() => handleSort('cw_hours_night', true)}>Night{getSortIndicator('cw_hours_night')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_body', true)}>%{getSortIndicator('benefit_body')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_mind', true)}>%{getSortIndicator('benefit_mind')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('benefit_social', true)}>%{getSortIndicator('benefit_social')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }}>Hours</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('CustomerCareTypeID')}>Care Type {getSortIndicator('CustomerCareTypeID')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', top: 39, textAlign: 'center' }} onClick={() => handleSort('GeneralCategory')}>General Category {getSortIndicator('GeneralCategory')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems
                                .filter(item => !isDefaultActivity(item))
                                .map((item, index) => {
                                    const actualIndex = lineItems.findIndex(li => li.activity_id === item.activity_id);
                                    const actId = String(item.activity_id || item.ActivityID || '');
                                    return (
                                        <tr
                                            key={index}
                                            className={isZeroPrice(item.multiplier) ? 'zero-price-row-flag' : undefined}
                                            style={bulkRemoveActivityIds.has(actId) ? { backgroundColor: '#ffe5e5' } : {}}
                                        >
                                            {edit && (
                                                <td style={{ whiteSpace: 'nowrap', paddingLeft: '20px', paddingRight: '12px', verticalAlign: 'middle' }}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        className="d-inline-block me-2"
                                                        checked={bulkRemoveActivityIds.has(actId)}
                                                        onChange={(e) => {
                                                            const newSet = new Set(bulkRemoveActivityIds);
                                                            e.target.checked ? newSet.add(actId) : newSet.delete(actId);
                                                            setBulkRemoveActivityIds(newSet);
                                                        }}
                                                    />
                                                    {renderRemoveActivityButton(item, actualIndex)}
                                                </td>
                                            )}
                                            <td style={{ paddingLeft: '12px', verticalAlign: 'middle' }}>
                                                {renderActivityNameCell(item)}
                                            </td>
                                            <td>{renderMultiplierCell(item, actualIndex)}</td>
                                            <td>{getFrequencyValue(item)}</td>
                                            <td>{((getHoursValue(item, 'sn_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                            <td>{((getHoursValue(item, 'sn_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                            <td>{((getHoursValue(item, 'ena_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                            <td>{((getHoursValue(item, 'ena_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                            <td>{((getHoursValue(item, 'cw_hours_day') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                            <td>{((getHoursValue(item, 'cw_hours_night') || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_body') || 0)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_body').toFixed(2)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_mind') || 0)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_mind').toFixed(2)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{(getBenefitValue(item, 'benefit_social') || 0)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{calculateBenefit(item, 'benefit_social').toFixed(2)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{item.customer_care_type_id}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{item.general_category}</td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </Table>
                </div>
                <div className="pqd-actions">
                    {edit ? (
                        <>
                            {/* Restore original line items and recalculate totals on cancel */}
                            <Button variant="secondary" onClick={() => {
                                showConfirm(
                                    "Are you sure you want to cancel? Any unsaved changes will be lost.",
                                    () => {
                                        setEdit(false);
                                        setBulkRemoveActivityIds(new Set());
                                        const fetchOriginalCarePlan = async () => {
                                            try {
                                                const response = await fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${carePlanId}`);
                                                if (response.ok) {
                                                    const data = await response.json();
                                                    setCarePlan(data.carePlan);
                                                    setLineItems(data.lineItems);
                                                    setFilteredItems(data.lineItems);
                                                    setTemp(data.lineItems);

                                                    recalculateTotals(data.lineItems);

                                                    const calculatedBenefitTotals = data.lineItems.reduce(
                                                        (benefitsTotals, item) => {
                                                            const multiplier = getMultiplier(item.multiplier);
                                                            const frequency = parseFloat(getFrequencyValue(item)) || 1;
                                                            const totalHours =
                                                                Number(getHoursValue(item, 'sn_hours_day')) +
                                                                Number(getHoursValue(item, 'sn_hours_night')) +
                                                                Number(getHoursValue(item, 'ena_hours_day')) +
                                                                Number(getHoursValue(item, 'ena_hours_night')) +
                                                                Number(getHoursValue(item, 'cw_hours_day')) +
                                                                Number(getHoursValue(item, 'cw_hours_night'));
                                                            return {
                                                                benefit_body: benefitsTotals.benefit_body + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_body')) / 100) || 0)),
                                                                benefit_mind: benefitsTotals.benefit_mind + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_mind')) / 100) || 0)),
                                                                benefit_social: benefitsTotals.benefit_social + (totalHours * frequency * multiplier * ((Number(getBenefitValue(item, 'benefit_social')) / 100) || 0)),
                                                            };
                                                        },
                                                        { benefit_body: 0, benefit_mind: 0, benefit_social: 0 }
                                                    );
                                                    setbenefitsTotals({
                                                        benefit_body: calculatedBenefitTotals.benefit_body.toFixed(2),
                                                        benefit_mind: calculatedBenefitTotals.benefit_mind.toFixed(2),
                                                        benefit_social: calculatedBenefitTotals.benefit_social.toFixed(2),
                                                    });
                                                } else {
                                                    console.error('Failed to fetch original service plan data');
                                                }
                                            } catch (error) {
                                                console.error('An error occurred while fetching the original service plan:', error);
                                            }
                                        };
                                        fetchOriginalCarePlan();
                                    },
                                    { title: 'Cancel Changes', confirmLabel: 'Discard Changes', confirmVariant: 'warning' }
                                );
                            }} className="me-2">Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSaveEdit}
                                className="me-2"
                                disabled={!hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE) || isSaving}
                                style={{
                                    cursor: (hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE) && !isSaving) ? 'pointer' : 'not-allowed',
                                    opacity: hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE) ? 1 : 0.5,
                                    minWidth: '130px'
                                }}
                            >
                                {isSaving ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</> : 'Save Changes'}
                            </Button>
                            <Button disabled={isSaving} variant="outline-primary" onClick={handleSaveAsNew} style={{ minWidth: '150px' }}>
                                {isSaving ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</> : 'Save As New Plan'}
                            </Button>
                        </>
                    ) : (
                        <>
                            {/* Store current items in temp state when entering edit mode */}
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    isRemovedPlan
                                        ? <Tooltip>Removed service plans cannot be edited.</Tooltip>
                                        : !hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE)
                                            ? <Tooltip>You do not have permission to edit service plans.</Tooltip>
                                            : <></>
                                }
                            >
                                <span>
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            if (!isRemovedPlan && hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE)) {
                                                setEdit(true);
                                                setTemp([...lineItems]);
                                            }
                                        }}
                                        disabled={isRemovedPlan || !hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE)}
                                        className="me-2"
                                        style={{
                                            cursor: (!isRemovedPlan && hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE)) ? 'pointer' : 'not-allowed',
                                            opacity: (!isRemovedPlan && hasPermission(PERMISSIONS.SERVICE_PLAN_UPDATE)) ? 1 : 0.5
                                        }}
                                    >
                                        Edit Plan
                                    </Button>
                                </span>
                            </OverlayTrigger>
                            <PermissionGuard
                                permission={PERMISSIONS.QUOTATION_CREATE}
                                showDisabled
                                tooltip={isRemovedPlan
                                    ? 'Cannot create a quotation for a removed service plan.'
                                    : 'You do not have permission to create quotations'}
                            >
                                <Button
                                    variant="success"
                                    onClick={handleCreateQuoteClick}
                                    disabled={creatingQuote || isRemovedPlan}
                                    style={{
                                        cursor: isRemovedPlan ? 'not-allowed' : 'pointer',
                                        opacity: isRemovedPlan ? 0.5 : 1
                                    }}
                                >
                                    {creatingQuote ? 'Creating Quote...' : 'Create Quote'}
                                </Button>
                            </PermissionGuard>
                        </>
                    )}
                </div>
                </div>
            </div>

            {/* Quote Creation Confirmation Modal */}
            <Modal show={showQuoteConfirmation} onHide={handleCancelQuote} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Quote Creation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to create a quotation for this service plan?</p>
                    <p><strong>Service Plan ID:</strong> {formatCarePlanId(carePlan?.care_plan_id)}</p>
                    <p><strong>Customer:</strong> {customerDetails ? `${customerDetails.name} ${customerDetails.surname}` : 'Loading...'}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelQuote}>
                        No, Cancel
                    </Button>
                    <Button variant="success" onClick={handleCreateQuote}>
                        Yes, Create Quote
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={confirmModal.show} onHide={() => setConfirmModal(m => ({ ...m, show: false }))} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirmModal.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{confirmModal.message}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmModal(m => ({ ...m, show: false }))}>
                        Cancel
                    </Button>
                    <Button
                        variant={confirmModal.confirmVariant}
                        onClick={() => {
                            if (confirmModal.onConfirm) confirmModal.onConfirm();
                            setConfirmModal(m => ({ ...m, show: false }));
                        }}
                    >
                        {confirmModal.confirmLabel}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Site selection modal — Create New Plan or Create Quote when multiple sites */}
            <Modal show={siteSelectModal.show} onHide={() => setSiteSelectModal(m => ({ ...m, show: false }))} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select a Site</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: 'var(--theme-text-secondary)', marginBottom: '16px' }}>
                        {siteSelectModal.purpose === 'quote'
                            ? 'This service plan is not linked to a site. Which site should the quotation use for price items?'
                            : 'This customer is linked to multiple sites. Which site should the new service plan be created under?'}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {siteSelectModal.sites.map(s => (
                            <button
                                key={s.siteId}
                                onClick={() => {
                                    setSiteSelectModal(m => ({ ...m, show: false }));
                                    if (siteSelectModal.purpose === 'quote') {
                                        setPendingQuoteSite(s);
                                        setShowQuoteConfirmation(true);
                                    } else {
                                        navigate(`/careplans/${id}/new?tenantId=${s.tenantId}&siteId=${s.siteId}`);
                                    }
                                }}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                                    padding: '12px 16px', border: '1px solid #dee2e6', borderRadius: '8px',
                                    background: 'var(--theme-surface-muted)', cursor: 'pointer', textAlign: 'left',
                                    transition: 'background 0.15s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#e9ecef'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f8f9fa'}
                            >
                                <span style={{ fontWeight: 600, color: 'var(--theme-text)', fontSize: '0.95rem' }}>{s.siteName}</span>
                                {s.tenantName && (
                                    <span style={{ fontSize: '0.8rem', color: 'var(--theme-text-muted)', marginTop: '2px' }}>{s.tenantName}</span>
                                )}
                            </button>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSiteSelectModal(m => ({ ...m, show: false }))}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {toast.show && <div className={`app-toast app-toast-${toast.variant}`}>{toast.message}</div>}
        </div>
    );
}

export default CarePlanDetail;
